import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  FileText, 
  CheckCircle, 
  AlertCircle,
  Loader2,
  Trash2,
  Save,
  FileUp,
  Building2,
  Edit2
} from 'lucide-react';
import { useExpenses } from '../contexts/ExpenseContext';
import * as pdfjsLib from 'pdfjs-dist';
// @ts-ignore - Vite URL import for worker
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

// Set up PDF.js worker - use bundled worker via Vite
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

type CompanyType = 'Bennett' | 'Auto Driveaway' | 'Coldiron' | 'Quality Driveaway' | 'Dynamic' | 'Horizon' | 'Lakefront' | 'Other';

interface ParsedSettlement {
  id: string;
  company: CompanyType;
  date: string;
  controlNumber: string;
  terminal: string;
  origin: string;
  destination: string;
  miles: number;
  lineHaul: number;
  fuelSurcharge: number;
  tolls?: number;
  otherPay: number;
  totalPay: number;
  deductions: number;
  advances?: number;
  escrow?: number;
  checkAmount: number;
  selected: boolean;
  isYTD?: boolean; // True if from Year-to-Date report
}

export default function ImportSettlement() {
  const { addExpense, expenses } = useExpenses();
  const [pdfText, setPdfText] = useState('');
  const [parsedSettlements, setParsedSettlements] = useState<ParsedSettlement[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [importStatus, setImportStatus] = useState<{ success: number; errors: number; skipped: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Manual entry form state
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [editingSettlementId, setEditingSettlementId] = useState<string | null>(null);
  const [manualEntry, setManualEntry] = useState({
    company: 'Lakefront' as CompanyType,
    date: '',
    controlNumber: '',
    origin: '',
    destination: '',
    miles: '',
    grossPay: '',
    netPay: ''
  });
  
  // Target tax year for import (auto-detected from settlement dates, but can be overridden)
  const currentYear = new Date().getFullYear();
  const [targetYear, setTargetYear] = useState<number | 'auto'>('auto');
  const availableYears = Array.from({ length: currentYear - 2019 }, (_, i) => currentYear - i);

  // Check if a settlement already exists in the database
  const isDuplicate = useCallback((settlement: { controlNumber: string; date: string; totalPay: number }): boolean => {
    return expenses.some(exp => {
      // Check by control number (for proper settlements)
      if (settlement.controlNumber && !settlement.controlNumber.startsWith('MANUAL-')) {
        if (exp.freightBillNumber === settlement.controlNumber || exp.filename?.includes(settlement.controlNumber)) {
          return true;
        }
      }
      
      // Also check by date + amount (for any settlement including manual ones)
      if (exp.date === settlement.date && Math.abs((exp.amount || 0) - settlement.totalPay) < 0.01) {
        return true;
      }
      
      return false;
    });
  }, [expenses]);

  // Parse Auto Driveaway "Purchase by Vendor Detail" summary report
  // Format: | Bill | 1/3/2023 | 080-122295-033 | $304.56 | 50120 - Drivers:50121 - Contract Driver Pay |
  const parseAutoDriveawaySummaryReport = (text: string): ParsedSettlement[] => {
    const settlements: ParsedSettlement[] = [];
    
    // Check if this is a summary report (has "Custom Purchase by Vendor Detail" or multiple BOL entries)
    if (!text.includes('Purchase by Vendor Detail') && !text.includes('Auto Driveaway Systems')) {
      // Try to detect by pattern - multiple lines with BOL format
      const bolPattern = /\d{3}-\d{6}-\d{3}/g;
      const bolMatches = text.match(bolPattern);
      if (!bolMatches || bolMatches.length < 3) {
        return []; // Not a summary report
      }
    }
    
    console.log('Auto Driveaway: Detected summary report format');
    
    // Group line items by BOL number
    const bolGroups: Record<string, { 
      date: string;
      driverPay: number;
      fuel: number;
      tolls: number;
      deductions: number;
      tripExpense: number;
      pickup: number;
      firstService: number;
      fuelSurcharge: number;
      repair: number;
    }> = {};
    
    // Handle TWO formats:
    // 1. Pipe-separated table (from PDF): |  Bill | 1/24/2024 | 080-154787-033 | Comdata C | $50.00 | 50120 - Drivers:50121...  |
    // 2. Non-pipe pasted text: Bill 1/3/2023 080-122295-033 $304.56 50120 - Drivers:50121...
    
    // FIRST: Try pipe-separated table format (from PDF copy)
    // Pattern: | Bill | DATE | BOL | CODE | AMOUNT | CATEGORY |
    const pipePattern = /\|\s*Bill\s*\|\s*(\d{1,2}\/\d{1,2}\/\d{4})\s*\|\s*(\d{3}-\d{6}-\d{3})(?:\([^)]*\))?\s*\|\s*(?:Comdata C|CD\d{2})\s*\|\s*(\(?\$?[\d,]+\.?\d*\)?)\s*\|\s*([^|]+)/gi;
    
    let pipeMatches = [...text.matchAll(pipePattern)];
    console.log(`AD Summary: Found ${pipeMatches.length} pipe-format entries`);
    
    // Process pipe-format entries
    for (const match of pipeMatches) {
      const [fullMatch, dateStr, bolNumber, amountStr, category] = match;
      
      // Parse amount - handle ($50.00) for negative
      let amount = 0;
      const cleanAmount = amountStr.replace(/[,$\s$]/g, '');
      if (amountStr.includes('(')) {
        amount = -Math.abs(parseFloat(cleanAmount.replace(/[()]/g, '')) || 0);
      } else {
        amount = parseFloat(cleanAmount) || 0;
      }
      
      processLineItem(bolGroups, dateStr, bolNumber, amount, category);
    }
    
    // SECOND: If no pipe matches found, try non-pipe format (pasted text)
    // Format: Bill DATE BOL [Comdata C|CDXX] AMOUNT CATEGORY
    // Example: Bill 1/24/2024 080-154787-033 Comdata C $50.00 50120 - Drivers:50121 - Contract Driver Pay
    if (pipeMatches.length === 0) {
      console.log('AD Summary: No pipe format found, trying non-pipe format...');
      
      // Use a global regex to find ALL entries in the continuous text
      // Pattern: Bill DATE BOL (optional transaction ID) CODE AMOUNT CATEGORY
      const noPipePattern = /Bill\s+(\d{1,2}\/\d{1,2}\/\d{4})\s+(\d{3}-\d{6}-\d{3})(?:\([^)]*\))?\s+(?:Comdata C|CD\d{2})\s+(\(?\$?[\d,]+\.?\d*\)?)\s+(\d{5}\s*-\s*[^B]+?)(?=\s*Bill\s+\d|$)/gi;
      
      const noPipeMatches = [...text.matchAll(noPipePattern)];
      console.log(`AD Summary: Found ${noPipeMatches.length} non-pipe entries`);
      
      for (const match of noPipeMatches) {
        const [fullMatch, dateStr, bolNumber, amountStr, category] = match;
        
        // Parse amount - handle ($50.00) for negative
        let amount = 0;
        const cleanAmount = amountStr.replace(/[,$\s$]/g, '');
        if (amountStr.includes('(')) {
          amount = -Math.abs(parseFloat(cleanAmount.replace(/[()]/g, '')) || 0);
        } else {
          amount = parseFloat(cleanAmount) || 0;
        }
        
        console.log(`AD NoPipe: BOL=${bolNumber}, Date=${dateStr}, Amt=${amount}, Cat=${category.substring(0,40)}`);
        processLineItem(bolGroups, dateStr, bolNumber, amount, category.trim());
      }
    }
    
    // Helper function to process a line item
    function processLineItem(groups: typeof bolGroups, dateStr: string, bolNumber: string, amount: number, category: string) {
      console.log(`AD Summary: BOL=${bolNumber}, Date=${dateStr}, Amount=${amount}, Cat=${category.substring(0,30)}`);
      
      if (!groups[bolNumber]) {
        groups[bolNumber] = {
          date: dateStr,
          driverPay: 0,
          fuel: 0,
          tolls: 0,
          deductions: 0,
          tripExpense: 0,
          pickup: 0,
          firstService: 0,
          fuelSurcharge: 0,
          repair: 0
        };
      }
      
      // Update date to latest
      const existingParts = groups[bolNumber].date.match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
      const newParts = dateStr.match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
      if (existingParts && newParts) {
        let existingYear = existingParts[3].length === 2 ? '20' + existingParts[3] : existingParts[3];
        let newYear = newParts[3].length === 2 ? '20' + newParts[3] : newParts[3];
        const existingDate = new Date(parseInt(existingYear), parseInt(existingParts[1])-1, parseInt(existingParts[2]));
        const newDate = new Date(parseInt(newYear), parseInt(newParts[1])-1, parseInt(newParts[2]));
        if (newDate > existingDate) {
          groups[bolNumber].date = dateStr;
        }
      }
      
      // Categorize amount
      const catLower = category.toLowerCase();
      if (catLower.includes('50121') || catLower.includes('contract driver pay')) {
        groups[bolNumber].driverPay += amount;
      } else if (catLower.includes('51303') || catLower.includes('all fuel cost')) {
        groups[bolNumber].fuel += amount;
      } else if (catLower.includes('51301') || catLower.includes('fuel surcharge')) {
        groups[bolNumber].fuelSurcharge += amount;
      } else if (catLower.includes('51305')) {
        groups[bolNumber].fuelSurcharge += amount;
      } else if (catLower.includes('51108') || catLower.includes('tolls')) {
        groups[bolNumber].tolls += amount;
      } else if (catLower.includes('51105') || catLower.includes('occ acc')) {
        groups[bolNumber].deductions += Math.abs(amount);
      } else if (catLower.includes('50124') || catLower.includes('trip related')) {
        groups[bolNumber].tripExpense += amount;
      } else if (catLower.includes('50301') || catLower.includes('pickup')) {
        groups[bolNumber].pickup += amount;
      } else if (catLower.includes('51302') || catLower.includes('first service')) {
        groups[bolNumber].firstService += amount;
      } else if (catLower.includes('51110') || catLower.includes('repair')) {
        groups[bolNumber].repair += amount;
      } else if (catLower.includes('50801') || catLower.includes('title')) {
        groups[bolNumber].tripExpense += amount;
      } else if (catLower.includes('51113') || catLower.includes('vin') || catLower.includes('smog')) {
        groups[bolNumber].tripExpense += amount;
      }
    }
    
    
    // Convert groups to settlements
    for (const [bolNumber, data] of Object.entries(bolGroups)) {
      // Parse date to YYYY-MM-DD
      const dateParts = data.date.match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
      let formattedDate = '';
      if (dateParts) {
        let year = dateParts[3];
        if (year.length === 2) {
          year = parseInt(year) > 50 ? '19' + year : '20' + year;
        }
        formattedDate = `${year}-${dateParts[1].padStart(2, '0')}-${dateParts[2].padStart(2, '0')}`;
      }
      
      // Calculate totals
      const lineHaul = data.driverPay;
      const fuelTotal = data.fuel + data.fuelSurcharge + data.firstService;
      const otherPay = data.tripExpense + data.pickup + data.repair;
      const totalPay = lineHaul + fuelTotal + data.tolls + otherPay;
      const checkAmount = totalPay - data.deductions;
      
      // Skip if no real payment
      if (totalPay === 0 && checkAmount === 0) continue;
      
      settlements.push({
        id: crypto.randomUUID(),
        company: 'Auto Driveaway',
        date: formattedDate,
        controlNumber: bolNumber,
        terminal: 'AD',
        origin: '',
        destination: '',
        miles: 0, // Not available in summary report
        lineHaul,
        fuelSurcharge: fuelTotal,
        tolls: data.tolls,
        otherPay,
        totalPay,
        deductions: data.deductions,
        checkAmount,
        selected: true
      });
    }
    
    console.log(`Auto Driveaway: Parsed ${settlements.length} settlements from summary report`);
    return settlements;
  };

  // Parse Auto Driveaway individual settlement (single BOL)
  const parseAutoDriveawaySettlement = (text: string): ParsedSettlement | null => {
    try {
      // Look for Auto Driveaway specific patterns
      // BOL format: 048-024596-033
      const bolMatch = text.match(/(?:BOL|breakdown of pay for BOL)\s*[\n\s|]*(\d{3}-\d{6}-\d{3})/i) ||
                      text.match(/(\d{3}-\d{6}-\d{3})/);
      if (!bolMatch) return null;
      
      const bolNumber = bolMatch[1];
      
      // Extract date (MM/DD/YYYY format) - look for "Advance loaded | 10/28/2025" or date at top
      const dateMatch = text.match(/(?:Advance loaded|loaded)\s*\|?\s*(\d{1,2}\/\d{1,2}\/\d{4})/i) ||
                       text.match(/^\s*(\d{1,2}\/\d{1,2}\/\d{4})/m);
      let formattedDate = '';
      if (dateMatch) {
        const parts = dateMatch[1].split('/');
        formattedDate = `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
      }
      
      // Extract miles - ".40 per mile X | 451" or "per mile X | 451"
      const milesMatch = text.match(/per mile\s*[X×x]\s*\|?\s*(\d+)/i) ||
                        text.match(/(\d+)\s*\|\s*\$[\d.]+\s*Fuel/i);
      const miles = milesMatch ? parseInt(milesMatch[1]) : 0;
      
      // Extract per mile rate pay - ".40 per mile X | 451 | $180.40"
      const milePayMatch = text.match(/\.(\d+)\s*per mile\s*[X×x]\s*\|?\s*\d+\s*\|?\s*\$?([\d,]+\.?\d*)/i) ||
                          text.match(/per mile\s*[X×x][^$]*\$?([\d,]+\.?\d*)/i);
      const milePay = milePayMatch ? parseFloat((milePayMatch[2] || milePayMatch[1]).replace(/,/g, '')) : 0;
      
      // Extract total pay for this move - "Total pay for this move | $465.10"
      const totalPayMatch = text.match(/Total pay for this move\s*\|?\s*\$?([\d,]+\.?\d*)/i);
      const totalPay = totalPayMatch ? parseFloat(totalPayMatch[1].replace(/,/g, '')) : 0;
      
      // Extract settlement amount - "Total Settlement | $184.70"
      const settlementMatch = text.match(/Total Settlement\s*\|?\s*\$?([\d,]+\.?\d*)/i);
      const checkAmount = settlementMatch ? parseFloat(settlementMatch[1].replace(/,/g, '')) : 0;
      
      // Extract advance - "Total Advance | $280.40"
      const advanceMatch = text.match(/Total Advance\s*\|?\s*\$?([\d,]+\.?\d*)/i);
      const advance = advanceMatch ? parseFloat(advanceMatch[1].replace(/,/g, '')) : 0;
      
      // Extract fuel - "Fuel allowance | $100.00" or "Fuel receipts turned in | $74.28"
      const fuelMatch = text.match(/Fuel (?:allowance|receipts)[^\d|]*\|?\s*\$?([\d,]+\.?\d*)/i);
      const fuel = fuelMatch ? parseFloat(fuelMatch[1].replace(/,/g, '')) : 0;
      
      // Extract tolls/permits - "Tolls & Permits | $5.40"
      const tollsMatch = text.match(/Tolls\s*[&]\s*Permits\s*\|?\s*\$?([\d,]+\.?\d*)/i);
      const tolls = tollsMatch ? parseFloat(tollsMatch[1].replace(/,/g, '')) : 0;
      
      // Extract pickup/travel - "Pickup/Travel | $150.00"
      const travelMatch = text.match(/Pickup\/Travel\s*\|?\s*\$?([\d,]+\.?\d*)/i);
      const travel = travelMatch ? parseFloat(travelMatch[1].replace(/,/g, '')) : 0;
      
      if (totalPay === 0 && checkAmount === 0) return null;
      
      const finalTotalPay = totalPay || (milePay + fuel + tolls + travel);
      
      return {
        id: crypto.randomUUID(),
        company: 'Auto Driveaway',
        date: formattedDate,
        controlNumber: bolNumber,
        terminal: 'AD',
        origin: '',
        destination: '',
        miles,
        lineHaul: milePay || (finalTotalPay - fuel - tolls - travel),
        fuelSurcharge: fuel,
        tolls,
        otherPay: travel,
        totalPay: finalTotalPay,
        deductions: advance,
        checkAmount: checkAmount || finalTotalPay,
        selected: true
      };
    } catch (e) {
      console.error('Auto Driveaway parse error:', e);
      return null;
    }
  };

  // Parse Coldiron Specialized Driveaway settlement (Remittance Advice format)
  const parseColdiron = (text: string): ParsedSettlement | null => {
    try {
      // Look for Coldiron specific pattern
      if (!text.includes('Coldiron') && !text.includes('Driver Card') && !text.includes('Remittance Advice')) return null;
      
      console.log('Coldiron: Found Coldiron content, parsing...');
      
      // Extract Invoice # - multiple patterns for linearized text
      // Pattern 1: "9/4/2025 | 266873 | LaVergn,TN"
      // Pattern 2: "270.97 266873 9/4/2025" (linearized - amount invoice date)
      // Pattern 3: "266873 9/4/2025 LaVergn,TN"
      const invoiceMatch = text.match(/Invoice #[:\s]*(\d{6})/i) || 
                          text.match(/\d{1,2}\/\d{1,2}\/\d{4}\s+(\d{6})\s+\w+/i) ||
                          text.match(/[\d.]+\s+(\d{6})\s+\d{1,2}\/\d{1,2}\/\d{4}/i) ||
                          text.match(/(\d{6})\s+\d{1,2}\/\d{1,2}\/\d{4}\s+[A-Za-z]/i) ||
                          text.match(/(\d{6})\s+[A-Z][a-z]+,?\s*[A-Z]{2}/i);
      if (!invoiceMatch) {
        console.log('Coldiron: No invoice number found');
        return null;
      }
      const invoiceNum = invoiceMatch[1];
      console.log('Coldiron: Invoice:', invoiceNum);
      
      // Extract Paid Date - "Paid Date 9/5/2025"
      const dateMatch = text.match(/Paid Date\s*(\d{1,2}\/\d{1,2}\/\d{4})/i);
      let formattedDate = '';
      if (dateMatch) {
        const parts = dateMatch[1].split('/');
        formattedDate = `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
      }
      console.log('Coldiron: Date:', formattedDate);
      
      // Extract location - "LaVergne,TN:Driver Pay" or "Columbus,OH:Driver Pay"
      const locationMatch = text.match(/([A-Z][a-zA-Z]+),?\s*([A-Z]{2}):Driver Pay/i) ||
                           text.match(/Ref[:\s]+([A-Za-z]+),?\s*([A-Z]{2})/i);
      const location = locationMatch ? `${locationMatch[1]}, ${locationMatch[2]}` : '';
      console.log('Coldiron: Location:', location);
      
      // Extract Driver Pay - "Driver Pay | 270.97" or "Driver Pay 270.97"
      const driverPayMatch = text.match(/Driver Pay[|\s]*([\d,]+\.?\d*)/i);
      const driverPay = driverPayMatch ? parseFloat(driverPayMatch[1].replace(/,/g, '')) : 0;
      
      // Extract Fuel Surcharge
      const fuelMatch = text.match(/Fuel Surcharge[|\s]*([\d,]+\.?\d*)/i);
      const fuel = fuelMatch ? parseFloat(fuelMatch[1].replace(/,/g, '')) : 0;
      
      // Extract Deadhead
      const deadheadMatch = text.match(/Deadhead[|\s]*([\d,]+\.?\d*)/i);
      const deadhead = deadheadMatch ? parseFloat(deadheadMatch[1].replace(/,/g, '')) : 0;
      
      // Extract Tolls
      const tollsMatch = text.match(/:Tolls[|\s]*([\d,]+\.?\d*)/i);
      const tolls = tollsMatch ? parseFloat(tollsMatch[1].replace(/,/g, '')) : 0;
      
      // Extract Total for Payment - "Total for Payment 684238 USD | 206.72" or "Total for Payment 684238 USD 206.72"
      const totalMatch = text.match(/Total for Payment[^|]*\|?\s*(?:USD)?\s*([\d,]+\.?\d*)/i) ||
                        text.match(/Total for Payment\s+\d+\s+USD\s+([\d,]+\.?\d*)/i) ||
                        text.match(/Total for Payment[^\d]*([\d,]+\.?\d*)\s*$/im);
      const checkAmount = totalMatch ? parseFloat(totalMatch[1].replace(/,/g, '')) : 0;
      console.log('Coldiron: Check amount:', checkAmount);
      
      // Calculate total pay (gross before deductions)
      const totalPay = driverPay + fuel + deadhead + tolls;
      console.log('Coldiron: Total pay:', totalPay);
      
      if (totalPay === 0 && checkAmount === 0) return null;
      
      return {
        id: crypto.randomUUID(),
        company: 'Coldiron',
        date: formattedDate,
        controlNumber: invoiceNum,
        terminal: 'CSD',
        origin: '',
        destination: location,
        miles: 0,
        lineHaul: driverPay,
        fuelSurcharge: fuel,
        tolls,
        otherPay: deadhead,
        totalPay: totalPay || checkAmount,
        deductions: totalPay > 0 ? totalPay - checkAmount : 0,
        checkAmount,
        selected: true
      };
    } catch (e) {
      console.error('Coldiron parse error:', e);
      return null;
    }
  };

  // Parse Quality Driveaway Pay Detail format (from portal)
  const parseQualityPayDetail = (section: string): ParsedSettlement | null => {
    try {
      // Must have "Pay Detail" to match this format
      if (!section.includes('Pay Detail')) return null;
      
      // Look for Driver # pattern
      const driverMatch = section.match(/Driver\s*#\s*(\d+)/i);
      if (!driverMatch) return null;
      
      console.log('QD PayDetail: Found Driver #', driverMatch[1]);
      
      // Extract date from "Pay Detail 5/13/2025 - 5/13/2025" or table rows
      const dateMatch = section.match(/Pay Detail\s+(\d{1,2})\/(\d{1,2})\/(\d{4})/i) ||
                       section.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
      let formattedDate = '';
      if (dateMatch) {
        formattedDate = `${dateMatch[3]}-${dateMatch[1].padStart(2, '0')}-${dateMatch[2].padStart(2, '0')}`;
      }
      console.log('QD PayDetail: Date:', formattedDate);
      
      // Extract Order # - look for 6-digit number in the table data
      // Format: "745526 5/13/2025 MACUNGIE, PA/"
      const orderMatch = section.match(/(\d{6})\s+\d{1,2}\/\d{1,2}\/\d{4}\s+[A-Z]/i);
      const orderNum = orderMatch ? orderMatch[1] : `QD-${formattedDate}`;
      console.log('QD PayDetail: Order #', orderNum);
      
      // Extract route: "MACUNGIE, PA/ to ROANOKE, IN/"
      const routeMatch = section.match(/([A-Z]+),?\s*([A-Z]{2})\/?\s+to\s+([A-Z]+),?\s*([A-Z]{2})/i);
      let origin = '';
      let destination = '';
      if (routeMatch) {
        origin = `${routeMatch[1].trim()}, ${routeMatch[2]}`;
        destination = `${routeMatch[3].trim()}, ${routeMatch[4]}`;
      }
      console.log('QD PayDetail: Route:', origin, '->', destination);
      
      // Extract Miles from Mileage Pay row: "Mileage Pay 601 0.8100"
      const milesMatch = section.match(/Mileage Pay\s+(\d{3,4})\s+[\d.]+/i);
      const miles = milesMatch ? parseInt(milesMatch[1]) : 0;
      console.log('QD PayDetail: Miles:', miles);
      
      // Extract Mileage Pay amount: "$486.81" after rate
      const mileagePayMatch = section.match(/Mileage Pay\s+\d+\s+[\d.]+\s+\$?([\d,]+\.?\d{2})/i);
      const mileagePay = mileagePayMatch ? parseFloat(mileagePayMatch[1].replace(/,/g, '')) : 0;
      console.log('QD PayDetail: Mileage Pay:', mileagePay);
      
      // Extract Fuel Surcharge: "Fuel Surcharge - Miles 601 0.3720 $223.57"
      const fuelMatch = section.match(/Fuel Surcharge[^$]*\$?([\d,]+\.?\d{2})/i);
      const fuel = fuelMatch ? parseFloat(fuelMatch[1].replace(/,/g, '')) : 0;
      console.log('QD PayDetail: Fuel:', fuel);
      
      // Extract Total Taxable Earnings: "$710.38"
      const totalMatch = section.match(/Total Taxable Earnings[^$]*\$?([\d,]+\.?\d{2})/i);
      const totalPay = totalMatch ? parseFloat(totalMatch[1].replace(/,/g, '')) : (mileagePay + fuel);
      console.log('QD PayDetail: Total Pay:', totalPay);
      
      // Extract Total Settlement Deductions: "($366.38)"
      const deductMatch = section.match(/Total Settlement Deductions[^(]*\(\$?([\d,]+\.?\d{2})\)/i);
      const deductions = deductMatch ? parseFloat(deductMatch[1].replace(/,/g, '')) : 0;
      console.log('QD PayDetail: Deductions:', deductions);
      
      // Extract Net Settlement: "$344.00"
      const netMatch = section.match(/Net Settlement[^$]*\$?([\d,]+\.?\d{2})/i);
      const checkAmount = netMatch ? parseFloat(netMatch[1].replace(/,/g, '')) : (totalPay - deductions);
      console.log('QD PayDetail: Net Settlement:', checkAmount);
      
      // Skip if Net Settlement is $0.00 (just carry-over entries)
      if (checkAmount === 0 && totalPay === 0) {
        console.log('QD PayDetail: Skipping $0 settlement');
        return null;
      }
      
      return {
        id: crypto.randomUUID(),
        company: 'Quality Driveaway',
        date: formattedDate,
        controlNumber: orderNum,
        terminal: 'QDA',
        origin,
        destination,
        miles,
        lineHaul: mileagePay,
        fuelSurcharge: fuel,
        tolls: 0,
        otherPay: 0,
        totalPay,
        deductions,
        checkAmount,
        selected: true
      };
    } catch (e) {
      console.error('QD PayDetail parse error:', e);
      return null;
    }
  };

  // Parse Quality Driveaway / Generic "Driver #" format settlements
  const parseQualityDriveaway = (section: string): ParsedSettlement | null => {
    try {
      // Skip if already handled by PayDetail parser
      if (section.includes('Pay Detail')) return null;
      
      // Look for Driver # pattern (must have this)
      const driverMatch = section.match(/Driver\s*#\s*(\d+)/i);
      if (!driverMatch) return null;
      
      console.log('Quality: Found Driver #', driverMatch[1]);
      
      // Extract Order # - look for 6-digit number followed by date
      // "759014 | 9/2/2025" or just "759014 9/2/2025"
      const orderMatch = section.match(/(\d{6})\s*\|?\s*\d{1,2}\/\d{1,2}\/\d{4}/i) ||
                        section.match(/Order #[:\s]*(\d+)/i);
      if (!orderMatch) {
        console.log('Quality: No order number found');
        return null;
      }
      const orderNum = orderMatch[1];
      console.log('Quality: Order #', orderNum);
      
      // Extract Date from table format or standalone
      const dateMatch = section.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
      let formattedDate = '';
      if (dateMatch) {
        formattedDate = `${dateMatch[3]}-${dateMatch[1].padStart(2, '0')}-${dateMatch[2].padStart(2, '0')}`;
      }
      console.log('Quality: Date:', formattedDate);
      
      // Extract route from Description: "GOSHEN,IN/ to FARIBAULT,MN/"
      const routeMatch = section.match(/([A-Z]+),\s*([A-Z]{2})\/?[^t]*to\s+([A-Z]+),\s*([A-Z]{2})/i);
      let origin = '';
      let destination = '';
      if (routeMatch) {
        origin = `${routeMatch[1].trim()}, ${routeMatch[2]}`;
        destination = `${routeMatch[3].trim()}, ${routeMatch[4]}`;
      }
      console.log('Quality: Route:', origin, '->', destination);
      
      // Extract Miles from Mileage Pay row: "Mileage Pay | 531"
      const milesMatch = section.match(/Mileage Pay\s*\|?\s*(\d{3,4})/i) || 
                        section.match(/(\d{3,4})\s*\|?\s*0\.\d{4}/);
      const miles = milesMatch ? parseInt(milesMatch[1]) : 0;
      
      // Extract Mileage Pay amount: look for dollar amount after "Mileage Pay"
      const mileagePayMatch = section.match(/Mileage Pay[^$\\]*\\?\$?([\d,]+\.?\d{2})/i);
      const mileagePay = mileagePayMatch ? parseFloat(mileagePayMatch[1].replace(/,/g, '')) : 0;
      console.log('Quality: Mileage Pay:', mileagePay);
      
      // Extract Flat Pay if no mileage pay - "Flat Pay Rate | $250.00"
      const flatPayMatch = section.match(/Flat Pay[^$\\]*\\?\$?([\d,]+\.?\d*)/i);
      const flatPay = flatPayMatch ? parseFloat(flatPayMatch[1].replace(/,/g, '')) : 0;
      console.log('Quality: Flat Pay:', flatPay);
      
      // Extract Fuel Surcharge: "Fuel Surcharge ... $148.68"
      const fuelMatch = section.match(/Fuel Surcharge[^$\\]*\\?\$?([\d,]+\.?\d{2})/i);
      const fuel = fuelMatch ? parseFloat(fuelMatch[1].replace(/,/g, '')) : 0;
      console.log('Quality: Fuel:', fuel);
      
      // Extract Total Taxable Earnings: "$493.83"
      const totalMatch = section.match(/Total Taxable Earnings[^$\\]*\\?\$?([\d,]+\.?\d{2})/i);
      const totalPay = totalMatch ? parseFloat(totalMatch[1].replace(/,/g, '')) : (mileagePay + flatPay + fuel);
      console.log('Quality: Total Pay:', totalPay);
      
      // Extract Total Settlement Deductions: "($230.47)" - parentheses for negative
      const deductMatch = section.match(/Total Settlement Deductions[^(]*\(\\?\$?([\d,]+\.?\d*)\)/i) ||
                         section.match(/Total Settlement Deductions[^$\\]*\\?\$?([\d,]+\.?\d*)/i);
      const deductions = deductMatch ? parseFloat(deductMatch[1].replace(/,/g, '')) : 0;
      
      // Extract Net Settlement: "$263.36"
      const netMatch = section.match(/Net Settlement[^$\\]*\\?\$?([\d,]+\.?\d{2})/i);
      const checkAmount = netMatch ? parseFloat(netMatch[1].replace(/,/g, '')) : (totalPay - deductions);
      console.log('Quality: Net Settlement:', checkAmount);
      
      if (totalPay === 0 && checkAmount === 0) {
        console.log('Quality: No amounts found');
        return null;
      }
      
      return {
        id: crypto.randomUUID(),
        company: 'Quality Driveaway',
        date: formattedDate,
        controlNumber: orderNum,
        terminal: 'QDA',
        origin,
        destination,
        miles,
        lineHaul: mileagePay || flatPay,
        fuelSurcharge: fuel,
        tolls: 0,
        otherPay: 0,
        totalPay,
        deductions,
        checkAmount,
        selected: true
      };
    } catch (e) {
      console.error('Quality Driveaway parse error:', e);
      return null;
    }
  };

  // Parse Horizon Transport Year-to-Date Settlement History format
  // Format: UNITNUMBER | CITYSTATE | CHECKAMT | CHECKDATE | CHECKTIME | SOURCE
  // Advance = first half of payment (at pickup), Settlement = second half (at delivery)
  // Total income per load = Advance + Settlement
  const parseHorizonYTD = (text: string): ParsedSettlement[] => {
    const settlements: ParsedSettlement[] = [];
    
    // Check if this is Horizon format - look for their column headers or typical patterns
    const isHorizon = text.includes('UNITNUMBER') && text.includes('CITYSTATE') && text.includes('CHECKAMT') ||
                      text.includes('Settlement') && (text.includes('Advance') || text.includes('Miscellaneous')) &&
                      (text.match(/[A-Z]{2,3}-[A-Z0-9]+/g) || []).length > 3; // Multiple unit numbers like BIS-121342
    
    if (!isHorizon) {
      return settlements;
    }
    
    console.log('Horizon: Detected Horizon Transport settlement format');
    
    // Normalize text - remove pipes and clean up
    let normalizedText = text.replace(/\|/g, ' ');
    
    // Insert line breaks before each unit number pattern (handles single-line pasted text)
    // Unit numbers look like: BIS-121342, MOR-N3058068, GEN-KO-NX433221, K&C-SV0432, etc.
    // Also handle RETURN- prefixed entries and special entries like "DOT INSPECTION", "DAMAGE DEPOSIT"
    normalizedText = normalizedText
      .replace(/\s+(BIS-\d+)/gi, '\n$1')
      .replace(/\s+(MOR-[A-Z0-9]+)/gi, '\n$1')
      .replace(/\s+(GEN-[A-Z0-9\-]+)/gi, '\n$1')
      .replace(/\s+(K&C-[A-Z0-9]+)/gi, '\n$1')
      .replace(/\s+(ANV-\d+)/gi, '\n$1')
      .replace(/\s+(FDR-\d+)/gi, '\n$1')
      .replace(/\s+(CW\d-\d+)/gi, '\n$1')
      .replace(/\s+(JC-[A-Z0-9]+)/gi, '\n$1')
      .replace(/\s+(JCS-[A-Z0-9]+)/gi, '\n$1')
      .replace(/\s+(FRP-[A-Z0-9]+)/gi, '\n$1')
      .replace(/\s+(FCH-[A-Z0-9]+)/gi, '\n$1')
      .replace(/\s+(G\d+-\d+)/gi, '\n$1')
      .replace(/\s+(GS-[A-Z0-9\/]+)/gi, '\n$1')
      .replace(/\s+(CM-[A-Z0-9]+)/gi, '\n$1')
      .replace(/\s+(EFee-[A-Z0-9]+)/gi, '\n$1')
      .replace(/\s+(RETURN-[A-Z0-9\-\/]+)/gi, '\n$1')
      .replace(/\s+(DOT INSPECTION)/gi, '\n$1')
      .replace(/\s+(DAMAGE DEPOSIT)/gi, '\n$1')
      .replace(/\s{2,}/g, ' '); // Collapse remaining multiple spaces
    
    // Split into lines
    const lines = normalizedText.split(/\n|\r\n?/).filter(l => l.trim());
    
    console.log('Horizon: Processing', lines.length, 'lines');
    
    // Track loads by unit number - combine Advance + Settlement for total pay
    const loadsMap = new Map<string, {
      unitNumber: string;
      destination: string;
      advance: number;
      settlement: number;
      advanceDate: string;
      settlementDate: string;
    }>();
    
    for (const line of lines) {
      // Skip header row and EFee entries (these are just $2 fees)
      if (line.includes('UNITNUMBER') || line.includes('CITYSTATE') || line.startsWith('EFee-')) {
        continue;
      }
      
      // Match settlement/advance lines
      // Format: UNITNUMBER CITYSTATE CHECKAMT CHECKDATE CHECKTIME SOURCE
      // Example: BIS-121342 KEARNEY, NE 567.07 24-Feb-22 13:51 Settlement
      
      // Try to extract unit number (alphanumeric with dashes/slashes)
      const unitMatch = line.match(/^([A-Z0-9\-\/]+)\s+/i);
      if (!unitMatch) continue;
      
      const unitNumber = unitMatch[1].trim();
      
      // Skip EFee entries and RETURN entries (handled separately)
      if (unitNumber.startsWith('EFee-')) continue;
      
      // Extract the rest of the line after unit number
      const remainder = line.substring(unitMatch[0].length);
      
      // Look for date pattern (DD-Mon-YY)
      const dateMatch = remainder.match(/(\d{1,2}-[A-Z][a-z]{2}-\d{2})/i);
      if (!dateMatch) continue;
      
      const dateStr = dateMatch[1];
      
      // Parse the date
      const dateParts = dateStr.match(/(\d{1,2})-([A-Z][a-z]{2})-(\d{2})/i);
      if (!dateParts) continue;
      
      const day = dateParts[1].padStart(2, '0');
      const monthNames: { [key: string]: string } = {
        'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04',
        'May': '05', 'Jun': '06', 'Jul': '07', 'Aug': '08',
        'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12'
      };
      const month = monthNames[dateParts[2]] || '01';
      const year = '20' + dateParts[3];
      const formattedDate = `${year}-${month}-${day}`;
      
      // Check source type at end of line
      const isSettlement = /Settlement\s*$/i.test(line) || /DD Settlement\s*$/i.test(line);
      const isAdvance = /Advance\s*$/i.test(line);
      const isMisc = /Miscellaneous\s*$/i.test(line);
      
      // Only process Advance and Settlement entries (both are income)
      if (!isSettlement && !isAdvance) continue;
      
      // Extract amount - look for number before the date
      const beforeDate = remainder.substring(0, remainder.indexOf(dateStr));
      const amountMatch = beforeDate.match(/([\d,]+\.?\d*)\s*$/);
      if (!amountMatch) continue;
      
      const amount = parseFloat(amountMatch[1].replace(/,/g, ''));
      if (isNaN(amount) || amount <= 0) continue;
      
      // Extract city/state - everything between unit number and amount
      const cityStateEnd = beforeDate.lastIndexOf(amountMatch[1]);
      let destination = beforeDate.substring(0, cityStateEnd).trim();
      
      // Clean up destination
      destination = destination.replace(/,\s*$/, '').trim();
      if (destination === ',') destination = '';
      
      // Use unit number as the key (combine advance + settlement for same load)
      if (!loadsMap.has(unitNumber)) {
        loadsMap.set(unitNumber, {
          unitNumber,
          destination: destination || '',
          advance: 0,
          settlement: 0,
          advanceDate: '',
          settlementDate: ''
        });
      }
      
      const load = loadsMap.get(unitNumber)!;
      
      // Update destination if we have a better one
      if (destination && !load.destination) {
        load.destination = destination;
      }
      
      if (isAdvance) {
        load.advance = amount;
        load.advanceDate = formattedDate;
        console.log(`  Horizon: Found advance ${unitNumber} - $${amount} on ${formattedDate}`);
      } else if (isSettlement) {
        load.settlement = amount;
        load.settlementDate = formattedDate;
        console.log(`  Horizon: Found settlement ${unitNumber} - $${amount} on ${formattedDate}`);
      }
    }
    
    // Convert to ParsedSettlement format - use settlement date (delivery date) as the record date
    for (const [unitNumber, load] of loadsMap) {
      const totalPay = load.advance + load.settlement;
      
      // Skip if no pay at all
      if (totalPay <= 0) continue;
      
      // Use settlement date if available, otherwise advance date
      const recordDate = load.settlementDate || load.advanceDate;
      if (!recordDate) continue;
      
      settlements.push({
        id: crypto.randomUUID(),
        company: 'Horizon',
        date: recordDate,
        controlNumber: unitNumber,
        terminal: '', // Horizon doesn't use terminals
        origin: '', // Horizon format only shows destination
        destination: load.destination,
        miles: 0,
        lineHaul: totalPay, // Total of advance + settlement
        fuelSurcharge: 0,
        tolls: 0,
        otherPay: 0,
        deductions: 0,
        advances: load.advance, // Track advance amount separately for reference
        escrow: 0,
        totalPay: totalPay,
        checkAmount: load.settlement, // The final settlement check amount
        selected: true,
        isYTD: true
      });
      
      console.log(`  Horizon: Load ${unitNumber} to ${load.destination} - Advance: $${load.advance} + Settlement: $${load.settlement} = Total: $${totalPay}`);
    }
    
    // Sort by date descending
    settlements.sort((a, b) => b.date.localeCompare(a.date));
    
    console.log('Horizon: Found', settlements.length, 'total loads');
    return settlements;
  };

  // Parse Bennett Year-to-Date Settlement History format
  // This handles the "SETTLEMENTS HISTORY LISTING" format with multiple orders
  const parseBennettYTD = (text: string): ParsedSettlement[] => {
    const settlements: ParsedSettlement[] = [];
    
    // Check if this is a Year-to-Date format
    if (!text.includes('SETTLEMENTS HISTORY LISTING') && !text.includes('SETLSTH')) {
      return settlements;
    }
    
    console.log('Bennett YTD: Detected Year-to-Date Settlement History format');
    
    // Normalize text - insert line breaks before key patterns if missing
    // This handles single-line PDFs where everything is concatenated
    // First, strip all pipe characters that act as column separators in tabular format
    let normalizedText = text
      .replace(/\|/g, ' ')  // Remove all pipe separators
      .replace(/(\s+)(ORIGIN\s*-->)/gi, '\n$2')  // Line break before ORIGIN
      .replace(/(\s+)(\*?\s*PAID\s+\d)/gi, '\n$2')  // Line break before each PAID entry
      .replace(/(---+)/g, '\n$1\n')  // Line breaks around dashes
      .replace(/\s{2,}/g, ' ');  // Collapse multiple spaces into one
    
    // Split text into lines for easier parsing
    const lines = normalizedText.split(/\n|\r\n?/).filter(l => l.trim());
    
    // Track orders as we find them
    interface OrderData {
      orderNum: string;
      terminal: string;
      date: string;
      lineHaul: number;
      fuelSurcharge: number;
      otherCredits: number;
      deductions: number;
      settlement: number;
      origin: string;
      destination: string;
    }
    
    const ordersMap = new Map<string, OrderData>();
    let currentOrigin = '';
    let currentDestination = '';
    
    for (const line of lines) {
      // Check for ORIGIN --> DESTINATION headers with state codes
      // Format: ORIGIN --> WAKARUSA IN DESTINATION --> ROCA NE
      const statePattern = '(IN|TX|OH|NE|WI|MO|IL|KS|OK|CO|AZ|NM|FL|GA|TN|KY|NC|SC|VA|WV|PA|NY|MI|MN|IA|ND|SD|MT|WY|ID|WA|OR|CA|NV|UT|AR|LA|MS|AL|MD|DE|NJ|CT|MA|NH|VT|ME|RI)';
      const routeRegex = new RegExp(`ORIGIN\\s*-->\\s*([A-Z\\s]+?)\\s+${statePattern}\\s+DESTINATION\\s*-->\\s*([A-Z\\s]+?)\\s+${statePattern}`, 'i');
      const routeMatch = line.match(routeRegex);
      if (routeMatch) {
        const originCity = routeMatch[1].trim();
        const originState = routeMatch[2].toUpperCase();
        const destCity = routeMatch[3].trim();
        const destState = routeMatch[4].toUpperCase();
        currentOrigin = `${originCity}, ${originState}`;
        currentDestination = `${destCity}, ${destState}`;
        console.log(`Bennett YTD: Found route - ${currentOrigin} → ${currentDestination}`);
        continue;
      }
      
      // Parse PAID lines - Format: "* PAID 212139 7692205 000 MIN 0020 003 00 6/03/22 DESCRIPTION [charge] [credit]"
      // The * at start indicates 1099 amount
      const paidMatch = line.match(/\*?\s*PAID\s+(\d+)\s+(\d{7})\s+000\s+([A-Z]{2,4})\s+\d+\s+(\d{3})\s+\d+\s+(\d{1,2}\/\d{1,2}\/\d{2})\s+(.+)/i);
      
      if (paidMatch) {
        const [, contractorId, orderNum, terminal, seqCode, date, remainder] = paidMatch;
        
        // Initialize order if not seen
        if (!ordersMap.has(orderNum)) {
          ordersMap.set(orderNum, {
            orderNum,
            terminal,
            date,
            lineHaul: 0,
            fuelSurcharge: 0,
            otherCredits: 0,
            deductions: 0,
            settlement: 0,
            origin: currentOrigin,
            destination: currentDestination
          });
        }
        
        const order = ordersMap.get(orderNum)!;
        
        // Parse the remainder to get description and amounts
        // Credits are at the end (line haul, FSC, loyalty rewards, etc.)
        // Charges are deductions (advances, escrow, etc.)
        
        // Extract the last number from the line - this is typically the amount
        // (in the original format, amounts are at the end after spaces)
        // Match numbers with or without decimals (300 or 300.00)
        const lastNumberMatch = remainder.match(/([\d,]+\.?\d*)\s*$/);
        const lastNumber = lastNumberMatch ? parseFloat(lastNumberMatch[1].replace(/,/g, '')) : 0;
        
        // Debug: Log what we're parsing
        if (/LEASE|BONUS/i.test(remainder)) {
          console.log(`  DEBUG: Found LEASE/BONUS line: "${remainder}" lastNumber=${lastNumber}`);
        }
        
        // FLAT RATE LINEHAUL - the amount at the end is the line haul
        if (/FLAT\s*RATE\s*(?:LINE|LINEHAUL)/i.test(remainder)) {
          // Try pattern with @ percentage first
          const lineHaulMatch = remainder.match(/FLAT\s*RATE\s*(?:LINE|LINEHAUL)?\s*([\d,.]+)\s*@\s*[\d.]+%\s*([\d,]+\.?\d*)/i);
          if (lineHaulMatch && lineHaulMatch[2]) {
            order.lineHaul = parseFloat(lineHaulMatch[2].replace(/,/g, ''));
          } else if (lastNumber > 0) {
            // Just use the last number on the line
            order.lineHaul = lastNumber;
          }
          console.log(`  Order ${orderNum}: FLAT RATE Line Haul = $${order.lineHaul}`);
        }
        
        // LINE HAUL PAY xxx.xx @ yy.00% zzz.zz (alternate format)
        else if (/LINE\s*HAUL\s*PAY/i.test(remainder)) {
          const lineHaulAltMatch = remainder.match(/LINE\s*HAUL\s*PAY\s*([\d,.]+)\s*@\s*[\d.]+%\s*([\d,]+\.?\d*)/i);
          if (lineHaulAltMatch && lineHaulAltMatch[2]) {
            order.lineHaul = parseFloat(lineHaulAltMatch[2].replace(/,/g, ''));
          } else if (lastNumber > 0) {
            order.lineHaul = lastNumber;
          }
          console.log(`  Order ${orderNum}: LINE HAUL PAY = $${order.lineHaul}`);
        }
        
        // FUEL SURCHARGE xxx.xx (credit at end of line)
        else if (/FUEL\s*SURCHARGE/i.test(remainder)) {
          if (lastNumber > 0) {
            order.fuelSurcharge = lastNumber;
          } else {
            const fscMatch = remainder.match(/FUEL\s*SURCHARGE\s*([\d,]+\.?\d*)/i);
            if (fscMatch) {
              order.fuelSurcharge = parseFloat(fscMatch[1].replace(/,/g, ''));
            }
          }
          console.log(`  Order ${orderNum}: FSC = $${order.fuelSurcharge}`);
        }
        
        // EFS SETTLEMENT xxx.xx (the final settlement check amount)
        else if (/EFS\s*SETTLEMENT/i.test(remainder)) {
          if (lastNumber > 0) {
            order.settlement = lastNumber;
          } else {
            const efsMatch = remainder.match(/(\d{8})?\s*EFS\s*SETTLEMENT\s*([\d,]+\.?\d*)/i);
            if (efsMatch && efsMatch[2]) {
              order.settlement = parseFloat(efsMatch[2].replace(/,/g, ''));
            }
          }
          console.log(`  Order ${orderNum}: EFS Settlement = $${order.settlement}`);
        }
        
        // Check for ALL credit types independently (not else-if, to catch multiple)
        // These are marked with * in the original document indicating 1099 income
        
        // LOYALTY REWARDS (credit)
        if (/LOYALTY\s*REWARDS/i.test(remainder) && lastNumber > 0) {
          order.otherCredits += lastNumber;
          console.log(`  Order ${orderNum}: Loyalty Rewards = $${lastNumber}`);
        }
        
        // LEASE ON BONUS (credit) - common on MIN terminal loads
        if (/LEASE\s*ON\s*BONUS/i.test(remainder)) {
          // Try to find any number in the line (might not be at the end)
          const bonusMatch = remainder.match(/(\d+\.?\d*)\s*$/);
          const bonusAmt = bonusMatch ? parseFloat(bonusMatch[1]) : lastNumber;
          if (bonusAmt > 0) {
            order.otherCredits += bonusAmt;
            console.log(`  Order ${orderNum}: Lease On Bonus = $${bonusAmt}`);
          } else {
            console.log(`  Order ${orderNum}: LEASE ON BONUS found but no amount! remainder="${remainder}"`);
          }
        }
        
        // TOLLS (credit)
        if (/\bTOLLS\b/i.test(remainder) && lastNumber > 0) {
          order.otherCredits += lastNumber;
          console.log(`  Order ${orderNum}: Tolls = $${lastNumber}`);
        }
        
        // TRANSPORTATION (credit - often for shuttle/deadhead pay)
        if (/\bTRANSPORTATION\b/i.test(remainder) && lastNumber > 0) {
          order.otherCredits += lastNumber;
          console.log(`  Order ${orderNum}: Transportation = $${lastNumber}`);
        }
        
        // FUEL UPON DELIVERY / FUEL PICKUP/DELIVERY (credit)
        if (/FUEL\s*(UPON|PICKUP)\s*\/?\s*DELIVERY/i.test(remainder) && lastNumber > 0) {
          order.otherCredits += lastNumber;
          console.log(`  Order ${orderNum}: Fuel Delivery = $${lastNumber}`);
        }
        
        // INSPECTION BONUS (credit)
        if (/INSPECTION\s*BONUS/i.test(remainder) && lastNumber > 0) {
          order.otherCredits += lastNumber;
          console.log(`  Order ${orderNum}: Inspection Bonus = $${lastNumber}`);
        }
      }
    }
    
    console.log('Bennett YTD: Found', ordersMap.size, 'unique orders');
    
    // Track which orders we've already added to settlements (for deduplication)
    const addedOrderNums = new Set<string>();
    
    // Convert map to settlements array
    for (const [orderNum, order] of ordersMap) {
      // Skip if we've already added this order
      if (addedOrderNums.has(orderNum)) {
        console.log(`  Skipping duplicate order ${orderNum}`);
        continue;
      }
      
      // Calculate total pay (line haul + FSC + other credits)
      const totalPay = order.lineHaul + order.fuelSurcharge + order.otherCredits;
      
      // Skip orders with no meaningful pay (need at least some line haul)
      if (order.lineHaul <= 0) {
        console.log(`  Skipping order ${orderNum}: No line haul pay`);
        continue;
      }
      
      // Mark as added
      addedOrderNums.add(orderNum);
      
      // Format date
      const dateParts = order.date.split('/');
      const month = dateParts[0].padStart(2, '0');
      const day = dateParts[1].padStart(2, '0');
      let year = dateParts[2];
      if (year.length === 2) {
        year = parseInt(year) > 50 ? '19' + year : '20' + year;
      }
      const formattedDate = `${year}-${month}-${day}`;
      
      // Use settlement amount if available, otherwise use totalPay
      const checkAmount = order.settlement > 0 ? order.settlement : totalPay;
      
      // Build description with order number for tracking
      const description = `Bennett Settlement - ${order.terminal} Terminal - ${order.origin} to ${order.destination} (#${orderNum})`;
      
      settlements.push({
        id: crypto.randomUUID(),
        company: 'Bennett',
        date: formattedDate,
        controlNumber: orderNum,
        terminal: order.terminal,
        origin: order.origin,
        destination: order.destination,
        miles: 0,
        lineHaul: order.lineHaul,
        fuelSurcharge: order.fuelSurcharge,
        tolls: 0, // YTD format doesn't have separate tolls field
        otherPay: order.otherCredits,
        deductions: order.deductions,
        advances: 0,
        escrow: 0,
        totalPay: totalPay,
        checkAmount: checkAmount,
        selected: true,
        isYTD: true,
        description // Add description with order number
      });
      
      console.log(`  Added settlement ${orderNum}: LH=$${order.lineHaul} FSC=$${order.fuelSurcharge} Other=$${order.otherCredits} Total=$${totalPay}`);
    }
    
    // Deduplicate by control number (order number) - each order should only appear once
    // This is safer than date+route+amount since you could run the same route twice in one day
    const seen = new Map<string, ParsedSettlement>();
    for (const settlement of settlements) {
      // Use control number as the unique key - this is the order number which should be unique
      const key = settlement.controlNumber;
      
      if (!seen.has(key)) {
        seen.set(key, settlement);
      } else {
        console.log(`  Removing duplicate order: ${settlement.controlNumber}`);
      }
    }
    
    const dedupedSettlements = Array.from(seen.values());
    
    // Sort by date descending
    dedupedSettlements.sort((a, b) => b.date.localeCompare(a.date));
    
    console.log('Bennett YTD: Total settlements after dedup:', dedupedSettlements.length, '(was', settlements.length, ')');
    return dedupedSettlements;
  };

  // Parse Bennett/Sennett settlement
  const parseBennettSettlement = (section: string): ParsedSettlement | null => {
    try {
      // If no Bennett-specific content, skip
      const hasBennettContent = section.includes('LINEHAUL') || 
                                section.includes('LINE HAUL') ||
                                section.includes('SETTLEMENT PAY') ||
                                section.includes('SETTLEMENTS STATEMENT') ||
                                section.includes('TOTAL PAY BEFORE DEDUCTIONS') ||
                                section.includes('BALANCE TO BE PAID') ||
                                section.includes('DRIVER RELIEF FUND') ||
                                section.includes('CHECK AMOUNT') ||
                                section.includes('FLAT RATE') ||
                                section.includes('STMT TOTAL');
      
      if (!hasBennettContent) {
        console.log('Bennett: No Bennett content found');
        return null;
      }
      
      console.log('Bennett: Found Bennett content, parsing...');
      
      // Check if this is the older "SETTLEMENTS STATEMENT" format
      const isOlderFormat = section.includes('SETTLEMENTS STATEMENT') || section.includes('Statement#');
      
      // Extract control number - handle both old and new formats
      // New format: "CONTROL #: 1179449"
      // Old format: "Statement#: 1162116"
      const controlMatch = section.match(/CONTROL\s*#[:\s]*(\d{7})/i) ||
                          section.match(/Statement\s*#[:\s]*(\d+)/i) ||
                          section.match(/(\d{7})\s+\d{8}\s+BILLED/i) ||
                          section.match(/\d{1,2}\/\d{1,2}\/\d{2}\s+(\d{7})\s+\d+/i) ||
                          section.match(/EFS\s*#[:\s]*(\d+)/i);
      
      const controlNumber = controlMatch ? controlMatch[1] : `BENNETT-${Date.now()}`;
      console.log('Bennett: Control number:', controlNumber, isOlderFormat ? '(older format)' : '');
      console.log('Bennett: Control number:', controlNumber);
      
      // Extract date - handle linearized format and older format
      // Prioritize 4-digit year patterns first!
      // New: "DATE: 08/20/25" or "DATE: 07/03/2025"
      // Linearized: "MCD 12/08/25 1185586" (terminal date control#)
      const dateMatch = section.match(/DATE:\s*(\d{1,2}\/\d{1,2}\/\d{4})/i) ||  // 4-digit year first
                       section.match(/DATE:\s*(\d{1,2}\/\d{1,2}\/\d{2})/i) ||   // Then 2-digit
                       section.match(/Check Date[:\s]*(\d{1,2}\/\d{1,2}\/\d{4})/i) ||
                       section.match(/(\d{1,2}\/\d{1,2}\/\d{4})\s+LINE HAUL/i) ||
                       section.match(/Entered:\s*(?:Processed:)?\s*(\d{1,2}\/\d{1,2}\/\d{2})/i) ||
                       // Linearized format: "MCD 12/08/25 1185586" (terminal date control#)
                       section.match(/[A-Z]{2,4}\s+(\d{1,2}\/\d{1,2}\/\d{2,4})\s+\d{6,7}/i) ||
                       // Any date pattern MM/DD/YY in the text
                       section.match(/\b(\d{1,2}\/\d{1,2}\/\d{2})\b/);
      const dateStr = dateMatch ? dateMatch[1] : '';
      console.log('Bennett: Date:', dateStr);
      
      // Extract terminal - handle both old and new formats
      // New: "TERMINAL: WGO" Old: "Terminal: DRD"
      // Make sure we don't match "DATE" from "DATE:" 
      let terminal = '';
      const terminalMatch = section.match(/TERMINAL[:\s]+([A-Z]{2,4})(?:\s|$)/i) ||
                           section.match(/Terminal[:\s]+([A-Z]{2,4})(?:\s|$)/i) ||
                           section.match(/\bCOMPANY:\s*28\s+TERMINAL:\s*([A-Z]{2,4})/i) ||
                           section.match(/\b28\s+([A-Z]{2,4})\s+\d{1,2}\/\d{1,2}\/\d{2}/i);
      if (terminalMatch && terminalMatch[1] !== 'DATE' && terminalMatch[1] !== 'EFS') {
        terminal = terminalMatch[1];
      }
      console.log('Bennett: Terminal:', terminal);
      
      // Extract origin and destination - handle linearized format
      // "ORIG: FOREST CITY, IA DEST: SYRACUSE, NY MILES:"
      const routeMatch = section.match(/ORIG[:\s]+([A-Z][A-Z\s]+,\s*[A-Z]{2})\s+DEST[:\s]+([A-Z][A-Z\s]+,\s*[A-Z]{2})/i) ||
                        section.match(/ORIG:\s*([^D]+?)DEST:\s*([^M]+?)MILES/i);
      const origin = routeMatch ? routeMatch[1].trim().replace(/\s+/g, ' ') : '';
      const destination = routeMatch ? routeMatch[2].trim().replace(/\s+/g, ' ') : '';
      
      // Extract miles - handle "MILES: 1076" or "BILLED MILES: 1,076"
      const milesMatch = section.match(/(?:BILLED\s*)?MILES[:\s]*([\d,]+)/i);
      const miles = milesMatch ? parseInt(milesMatch[1].replace(/,/g, '')) : 0;
      
      // Extract line haul pay - handle linearized "FLAT RATE LINEHAUL $645.60"
      const lineHaulMatch = section.match(/(?:FLAT\s*RATE\s*)?LINE\s*HAUL(?:\s*PAY)?[^$\d]*\$?([\d,]+\.?\d*)/i) ||
                           section.match(/LINEHAUL\s+\$?([\d,]+\.?\d*)/i);
      const lineHaul = lineHaulMatch ? parseFloat(lineHaulMatch[1].replace(/,/g, '')) : 0;
      
      // Extract fuel surcharge - handle "FUEL SURCHARGE $301.28"
      const fuelMatch = section.match(/FUEL\s*SURCHARGE\s+\$?([\d,]+\.?\d*)/i);
      const fuelSurcharge = fuelMatch ? parseFloat(fuelMatch[1].replace(/,/g, '')) : 0;
      
      // Extract tolls - handle "TOLLS $28.50"
      const tollsMatch = section.match(/TOLLS\s+\$?([\d,]+\.?\d*)/i);
      const tolls = tollsMatch ? parseFloat(tollsMatch[1].replace(/,/g, '')) : 0;
      
      // Extract check amount FIRST - "CHECK AMOUNT: $331.57" or in linearized "1,076 CHECK AMOUNT: $331.57"
      const checkMatch = section.match(/CHECK\s*AMOUNT[:\s]*\$?([\d,]+\.?\d*)/i) ||
                        section.match(/BALANCE\s*TO\s*BE\s*PAID[:\s|*]*\$?([\d,]+\.?\d*)/i);
      const checkAmount = checkMatch ? parseFloat(checkMatch[1].replace(/,/g, '')) : 0;
      console.log('Bennett: Check amount:', checkAmount);
      
      // Extract total pay - look for "$995.38" pattern after TOLLS or TOTAL PAY
      // In linearized: "TOLLS $28.50 11/03/25 $995.38"
      const totalPayMatch = section.match(/TOTAL\s*PAY\s*(?:BEFORE\s*DEDUCTIONS)?[:\s|*]*\$?([\d,]+\.?\d*)/i) ||
                           section.match(/TOLLS\s+\$[\d.]+\s+\d{1,2}\/\d{1,2}\/\d{2}\s+\$?([\d,]+\.?\d{2})/i) ||
                           section.match(/\$28\.50[^$]*\$([\d,]+\.?\d{2})/i);
      let totalPay = totalPayMatch ? parseFloat(totalPayMatch[1].replace(/,/g, '')) : 0;
      console.log('Bennett: Total pay from regex:', totalPay);
      
      // If no total pay found, calculate from components
      if (totalPay === 0 && lineHaul > 0) {
        totalPay = lineHaul + fuelSurcharge + tolls;
        console.log('Bennett: Total pay calculated:', totalPay);
      }
      
      // Extract total deductions
      const deductionsMatch = section.match(/TOTAL\s*DEDUCTIONS[:\s|*]*\$?\s*-?([\d,]+\.?\d*)/i) ||
                             section.match(/\$-?([\d,]+\.?\d*)\s*TOTAL\s*DEDUCTIONS/i);
      const deductions = deductionsMatch ? parseFloat(deductionsMatch[1].replace(/,/g, '')) : 0;
      
      // Skip void settlements
      if (checkAmount === 0 && section.toLowerCase().includes('void')) return null;
      
      // Calculate other pay (transportation, spotting, etc.)
      const otherPay = Math.max(0, totalPay - lineHaul - fuelSurcharge - tolls);
      
      // Format date for storage
      let formattedDate = '';
      if (dateStr) {
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          const month = parts[0].padStart(2, '0');
          const day = parts[1].padStart(2, '0');
          let year = parts[2];
          if (year.length === 2) year = '20' + year;
          formattedDate = `${year}-${month}-${day}`;
        }
      }
      
      // If we have any useful data, return the settlement
      if (totalPay === 0 && checkAmount === 0 && lineHaul === 0) {
        console.log('Bennett: No usable amounts found');
        return null;
      }
      
      // If we only have check amount, estimate total from check + deductions
      if (totalPay === 0 && checkAmount > 0) {
        totalPay = checkAmount + deductions;
        console.log('Bennett: Using check + deductions for total:', totalPay);
      }
      
      return {
        id: crypto.randomUUID(),
        company: 'Bennett',
        date: formattedDate,
        controlNumber,
        terminal,
        origin,
        destination,
        miles,
        lineHaul,
        fuelSurcharge,
        tolls,
        otherPay,
        totalPay,
        deductions,
        checkAmount,
        selected: true
      };
    } catch (e) {
      console.error('Bennett parse error:', e);
      return null;
    }
  };

  // Parse Dynamic Transport settlement (Travel Trailer transport from Goshen, IN)
  // Format: DYNAMIC DISSEMINATION/BENCHMARKS Driver Settlement with BOL#, Ship From/To, miles × rate
  const parseDynamic = (section: string): ParsedSettlement | null => {
    try {
      // Must have Dynamic content OR Goshen IN address (Dynamic HQ) OR typical Dynamic patterns
      const hasDynamicContent = section.includes('DYNAMIC') || 
                                section.includes('Dynamic') ||
                                section.includes('Goshen, IN 46526') ||
                                section.includes('574-333-3042') ||
                                (section.includes('Driver Settlement') && section.includes('Linehaul'));
      
      if (!hasDynamicContent) {
        return null;
      }
      
      // Skip if it's another company
      if (section.includes('Bennett') || section.includes('Coldiron') || section.includes('Quality Driveaway')) {
        return null;
      }
      
      console.log('Dynamic: Found Dynamic content, parsing...');
      
      // Extract Date - "Date: 9/15/2020" or first date in text
      const dateMatch = section.match(/Date:\s*(\d{1,2}\/\d{1,2}\/\d{4})/i) ||
                       section.match(/Final Pay:\s*(\d{1,2}\/\d{1,2}\/\d{4})/i) ||
                       section.match(/(\d{1,2}\/\d{1,2}\/\d{4})\s+2408/i) ||
                       section.match(/(\d{1,2}\/\d{1,2}\/\d{4})/);
      let formattedDate = '';
      if (dateMatch) {
        const parts = dateMatch[1].split('/');
        formattedDate = `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
      }
      console.log('Dynamic: Date:', formattedDate);
      
      // Extract BOL # - multiple patterns for different text formats
      // "BOL # 7216" or "SERIAL # 7216 328410" (BOL then serial) or just 4-digit number near Trip Details
      const bolMatch = section.match(/BOL\s*#\s*(\d{4,6}|[A-Z]{2,3}\d{4,6})/i) ||
                      section.match(/SERIAL\s*#\s*(\d{4,6})\s+\d+/i) ||
                      section.match(/Trip Details:.*?(\d{4,6})/i) ||
                      section.match(/(\d{4,6})\s+Elkhart/i) ||
                      section.match(/Elkhart[^0-9]*(\d{4,6})/i) ||
                      section.match(/([A-Z]{2}\d{6})/i);
      const bolNumber = bolMatch ? bolMatch[1] : `DYN-${Date.now()}`;
      console.log('Dynamic: BOL #:', bolNumber);
      
      // Extract Ship From and Ship To from Trip Details
      // Patterns vary: table format, linearized "Elkhart, IN Couch's - Hamilton OH", etc.
      let origin = '';
      let destination = '';
      
      // Try to find origin (usually Elkhart, IN for Dynamic)
      // Linearized: "Total: Elkhart, IN Couch's - Hamilton OH TYPE:"
      const originMatch = section.match(/Total:\s*([A-Za-z]+,?\s*[A-Z]{2})/i) ||
                         section.match(/SHIP FROM[:\s|]*([A-Za-z\s]+,?\s*[A-Z]{2})/i) ||
                         section.match(/(\w+),?\s*IN\s+[A-Z]/i) ||
                         section.match(/Elkhart,?\s*IN/i);
      if (originMatch) {
        origin = originMatch[1]?.trim() || 'Elkhart, IN';
      }
      
      // Try to find destination - often has dealer name like "Couch's - Hamilton OH"
      // Linearized: "Elkhart, IN Couch's - Hamilton OH TYPE:" or "Fun Town - Wharton TX"
      const destMatch = section.match(/(?:Elkhart,?\s*IN|Total:)\s+([A-Za-z][A-Za-z\s']+\s*-\s*[A-Za-z\s]+[A-Z]{2})(?:\s+TYPE|\s*$)/i) ||
                       section.match(/SHIP TO[:\s|]*([^|]+?)(?:\s*\||\s*TYPE|\s*Travel)/i) ||
                       section.match(/([A-Za-z][A-Za-z\s']+\s*-\s*[A-Za-z\s]+,?\s*[A-Z]{2})(?:\s+TYPE|\s+Travel|\s*$)/i) ||
                       section.match(/American Family\s*-\s*([A-Za-z\s]+[A-Z]{2})/i) ||
                       section.match(/Fun Town\s*-\s*([A-Za-z\s]+[A-Z]{2})/i);
      if (destMatch) {
        // Clean up destination - remove trailing TYPE, Travel Trailer, etc.
        destination = destMatch[1].trim()
          .replace(/\s+TYPE.*$/i, '')
          .replace(/\s+Travel.*$/i, '')
          .replace(/\s+/g, ' ');
      }
      console.log('Dynamic: Route:', origin, '->', destination);
      
      // Extract Miles and Rate from various formats:
      // Table: "Linehaul/Fuel Surcharge Rate | 191 | $1.67 | $318.97"
      // Linearized: "191 $1.67 $318.97 ... Linehaul/Fuel Surcharge Rate"
      // Flat rate: "1 $1,289.64 $1,289.64"
      let miles = 0;
      let lineHaul = 0;
      
      // Try standard format first: "Linehaul/Fuel Surcharge Rate | 191 | $1.67 | $318.97"
      let lineHaulMatch = section.match(/Linehaul\/Fuel Surcharge Rate\s*\|?\s*(\d{1,3})\s*\|?\s*\$?([\d,.]+)\s*\|?\s*\$?([\d,.]+)/i);
      
      // Try linearized format: "191 $1.67 $318.97 ... Linehaul" (miles, rate, amount)
      // Use \d{1,3} to avoid matching years like 2020
      if (!lineHaulMatch) {
        lineHaulMatch = section.match(/\b(\d{1,3})\s+\$?([0-3]\.\d{2})\s+\$?([\d,]+\.\d{2})\s+.*?Linehaul/i);
      }
      
      // Try to find miles × rate = amount pattern (2-3 digit miles, rate ~$1.xx-$2.xx)
      if (!lineHaulMatch) {
        // Look for pattern like "191 $1.67 $318.97" 
        // Miles: 2-3 digits (not 4 to avoid years)
        // Rate: $0.50-$3.00 range (per-mile rate)
        lineHaulMatch = section.match(/\b(\d{2,3})\s+\$?([0-3]\.\d{2})\s+\$?([\d,]+\.\d{2})/);
      }
      
      // Also check for flat rate format: "1 $1,289.64 $1,289.64"
      if (!lineHaulMatch) {
        lineHaulMatch = section.match(/\b(1)\s+\$?([\d,]+\.\d{2})\s+\$?([\d,]+\.\d{2})\s+.*?(?:Linehaul|Bond)/i);
      }
      
      if (lineHaulMatch) {
        const qty = parseInt(lineHaulMatch[1]);
        const rate = parseFloat(lineHaulMatch[2].replace(/,/g, ''));
        const amount = parseFloat(lineHaulMatch[3].replace(/,/g, ''));
        
        // Validate: qty shouldn't look like a year (2000-2030)
        if (qty >= 2000 && qty <= 2030) {
          console.log('Dynamic: Skipping qty that looks like a year:', qty);
          // Don't use this match - it's probably the year
        } else if (qty === 1 && amount > 500) {
          // If qty is 1 and amount is large, it's a flat rate (not per-mile)
          miles = 0; // Flat rate, no per-mile
          lineHaul = amount;
          console.log('Dynamic: Found flat rate linehaul:', amount);
        } else {
          miles = qty;
          lineHaul = amount;
          console.log('Dynamic: Found per-mile linehaul - miles:', qty, 'rate:', rate, 'amount:', amount);
        }
      }
      // If we found linehaul but no miles, estimate miles from amount (typical rate ~$1.65/mile)
      if (lineHaul > 0 && miles === 0) {
        // Estimate: Elkhart IN to Salem VA is ~680 miles, typical per-mile rate is $1.50-$1.80
        const estimatedMiles = Math.round(lineHaul / 1.65);
        if (estimatedMiles >= 50 && estimatedMiles <= 3000) {
          miles = estimatedMiles;
          console.log('Dynamic: Estimated miles from linehaul:', miles, '(from $', lineHaul, ')');
        }
      }
      
      console.log('Dynamic: Miles:', miles, 'Line Haul:', lineHaul);
      
      // Extract Tolls (positive reimbursement)
      const tollsMatch = section.match(/Tolls\s*\|?\s*\d*\s*\|?\s*\$?([\d,.]+)\s*\|?\s*\$?([\d,.]+)/i);
      let tolls = 0;
      if (tollsMatch) {
        tolls = parseFloat(tollsMatch[2]?.replace(/,/g, '') || tollsMatch[1]?.replace(/,/g, '') || '0');
      }
      
      // Extract Permits (positive reimbursement)
      const permitsMatch = section.match(/Permits\s*\|?\s*\d*\s*\|?\s*\$?([\d,.]+)\s*\|?\s*\$?([\d,.]+)/i);
      let permits = 0;
      if (permitsMatch) {
        permits = parseFloat(permitsMatch[2]?.replace(/,/g, '') || permitsMatch[1]?.replace(/,/g, '') || '0');
      }
      
      // Extract deductions
      // Bond Payment (negative)
      const bondMatch = section.match(/Bond(?:\s*Payment)?\s*\|?\s*\d*\s*\|?\s*-?\$?([\d,.]+)\s*\|?\s*-?\$?([\d,.]+)/i);
      const bond = bondMatch ? parseFloat(bondMatch[2]?.replace(/,/g, '') || bondMatch[1]?.replace(/,/g, '') || '0') : 0;
      
      // EFS fees (negative)
      const efsMatch = section.match(/EFS\s*\|?\s*(\d+)\s*\|?\s*-?\$?([\d,.]+)\s*\|?\s*-?\$?([\d,.]+)/i);
      const efsFees = efsMatch ? Math.abs(parseFloat(efsMatch[3]?.replace(/,/g, '') || '0')) : 0;
      
      // Pull-In Pre-Paid (negative)
      const pullInMatch = section.match(/Pull[- ]?In\s*Pre[- ]?Paid\s*\|?\s*\d*\s*\|?\s*-?\$?([\d,.]+)\s*\|?\s*-?\$?([\d,.]+)/i);
      const pullIn = pullInMatch ? Math.abs(parseFloat(pullInMatch[2]?.replace(/,/g, '') || pullInMatch[1]?.replace(/,/g, '') || '0')) : 0;
      
      // Calculate total deductions
      const deductions = bond + efsFees + pullIn;
      console.log('Dynamic: Deductions - Bond:', bond, 'EFS:', efsFees, 'Pull-In:', pullIn);
      
      // Extract Total from various formats:
      // "Total: | $299.97" or "Total: $299.97" or linearized "$299.97 Linehaul/Fuel"
      let totalMatch = section.match(/Total:\s*\|?\s*\$?([\d,.]+)/i) ||
                       section.match(/TOTAL TRIP PAYMENT:\s*\|?\s*\$?([\d,.]+)/i);
      
      // For linearized text, look for amount before "Linehaul" label
      if (!totalMatch) {
        totalMatch = section.match(/\$?([\d,]+\.\d{2})\s+Linehaul\/Fuel Surcharge Rate/i);
      }
      
      // Also try to find "Trip Payments:" total
      if (!totalMatch) {
        totalMatch = section.match(/TOTAL TRIP PAYMENT[:\s|]*\$?([\d,.]+)/i);
      }
      
      const totalPay = totalMatch ? parseFloat(totalMatch[1].replace(/,/g, '')) : 0;
      console.log('Dynamic: Total Pay:', totalPay);
      
      // Check amount is total minus the advance (settlement portion)
      // Linearized: "$140.97 TOTAL TRIP PAYMENT" or "Settlement | EFS | $140.97"
      let settlementMatch = section.match(/Settlement\s*\|?\s*EFS\s*\|?\s*\$?([\d,.]+)/i);
      if (!settlementMatch) {
        settlementMatch = section.match(/\$?([\d,]+\.\d{2})\s+TOTAL TRIP PAYMENT/i);
      }
      const checkAmount = settlementMatch ? parseFloat(settlementMatch[1].replace(/,/g, '')) : (totalPay > 0 ? totalPay : lineHaul);
      
      if (totalPay === 0 && lineHaul === 0) {
        console.log('Dynamic: No amounts found');
        return null;
      }
      
      return {
        id: crypto.randomUUID(),
        company: 'Dynamic',
        date: formattedDate,
        controlNumber: bolNumber,
        terminal: 'DYN',
        origin,
        destination,
        miles,
        lineHaul,
        fuelSurcharge: 0, // Included in lineHaul for Dynamic
        tolls,
        otherPay: permits,
        totalPay: totalPay || lineHaul + tolls + permits,
        deductions,
        checkAmount,
        selected: true
      };
    } catch (e) {
      console.error('Dynamic parse error:', e);
      return null;
    }
  };

  // Parse Lakefront Transport settlements (Hotshot - Pay Breakdown Sheets and Rate Confirmations)
  // Pay Breakdown format: Table with Broker | Pick up Date | Delivery Date | Gross Pay | Lakefront 25% | Net Due
  // Rate Con format: Line haul rate from various brokers
  const parseLakefront = (text: string): ParsedSettlement[] => {
    const settlements: ParsedSettlement[] = [];
    
    // Check for Lakefront indicators
    const isLakefront = text.includes('Lakefront') || 
                        text.includes('LAKEFRONT') ||
                        text.includes('LAkefront') ||
                        text.includes('986939') || // Lakefront MC number
                        text.includes('lakefronttransport');
    
    if (!isLakefront) return [];
    
    console.log('Lakefront: Found Lakefront content, parsing...');
    
    // Check if this is a Pay Breakdown Sheet (has columns like "Gross Pay", "25%", "Net Due")
    const isPayBreakdown = text.includes('Gross Pay') || 
                          text.includes('LAkefront 25%') ||
                          text.includes('Net Due Carrier') ||
                          text.includes('Amount to Carrier');
    
    if (isPayBreakdown) {
      console.log('Lakefront: Detected Pay Breakdown Sheet format');
      
      // The PDF linearizes the table, so we need to extract values differently
      // Look for "Net Due Carrier for Load" followed by dollar amounts - these are the key values
      
      // Strategy: Find the net due amounts, then work backwards to find related data
      // Pattern: "Net Due Carrier for Load $914.00 $575.00 $1,975.00 $1,113.11"
      const netDuePattern = /Net\s*Due\s*Carrier\s*(?:for\s*Load)?[^$]*(\$[\d,]+\.?\d*)[^$]*(\$[\d,]+\.?\d*)?[^$]*(\$[\d,]+\.?\d*)?[^$]*(\$[\d,]+\.?\d*)?/i;
      const netDueMatch = text.match(netDuePattern);
      
      // Also try linearized pattern: "$914.00 $575.00 $1,975.00 $1,113.11 $0.00 Net Due"
      const netAmounts: number[] = [];
      if (netDueMatch) {
        for (let i = 1; i <= 4; i++) {
          if (netDueMatch[i]) {
            const amt = parseFloat(netDueMatch[i].replace(/[$,]/g, ''));
            if (amt > 0) netAmounts.push(amt);
          }
        }
      }
      console.log('Lakefront: Found net amounts:', netAmounts);
      
      // Extract gross pay amounts
      const grossPattern = /Gross\s*Pay\s*(?:Total|1st\s*Pick)?[^$]*(\$[\d,]+\.?\d*)[^$]*(\$[\d,]+\.?\d*)?[^$]*(\$[\d,]+\.?\d*)?[^$]*(\$[\d,]+\.?\d*)?/i;
      const grossMatch = text.match(grossPattern);
      const grossAmounts: number[] = [];
      if (grossMatch) {
        for (let i = 1; i <= 4; i++) {
          if (grossMatch[i]) {
            const amt = parseFloat(grossMatch[i].replace(/[$,]/g, ''));
            if (amt > 0) grossAmounts.push(amt);
          }
        }
      }
      console.log('Lakefront: Found gross amounts:', grossAmounts);
      
      // Extract delivery dates - look for date patterns after "Delivery Date"
      const deliveryPattern = /Delivery\s*Date[^\d]*([\d\/\-]+(?:\/\d{2,4})?)[^\d]*([\d\/\-]+(?:\/\d{2,4})?)?[^\d]*([\d\/\-]+(?:\/\d{2,4})?)?[^\d]*([\d\/\-]+(?:\/\d{2,4})?)?/i;
      const deliveryMatch = text.match(deliveryPattern);
      const deliveryDates: string[] = [];
      if (deliveryMatch) {
        for (let i = 1; i <= 4; i++) {
          if (deliveryMatch[i]) {
            const dateStr = deliveryMatch[i].trim();
            const parts = dateStr.match(/(\d{1,2})[\/\-](\d{1,2})[\/\-]?(\d{2,4})?/);
            if (parts) {
              const month = parts[1].padStart(2, '0');
              const day = parts[2].padStart(2, '0');
              let year = parts[3] || '2020';
              if (year.length === 2) year = '20' + year;
              deliveryDates.push(`${year}-${month}-${day}`);
            }
          }
        }
      }
      console.log('Lakefront: Found delivery dates:', deliveryDates);
      
      // Extract broker names - look for common broker patterns in text
      const brokerNames = ['CHR', 'RR', 'Brock', 'Armstrong', 'A-1', 'Wide Range', 'WFX'];
      const foundBrokers: string[] = [];
      for (const broker of brokerNames) {
        if (text.includes(broker)) {
          foundBrokers.push(broker);
        }
      }
      console.log('Lakefront: Found brokers in text:', foundBrokers);
      
      // Create settlements from the extracted data
      // Use net amounts as the primary indicator of how many settlements we have
      const numSettlements = Math.max(netAmounts.length, grossAmounts.length, deliveryDates.length);
      
      for (let i = 0; i < numSettlements; i++) {
        const netDue = netAmounts[i] || 0;
        const grossPay = grossAmounts[i] || 0;
        const deliveryDate = deliveryDates[i] || '';
        const broker = foundBrokers[i] || `Load${i + 1}`;
        
        // Skip if no real pay data
        if (netDue === 0 && grossPay === 0) continue;
        
        // Calculate Lakefront cut (25% of gross)
        const lakefrontCut = grossPay > 0 ? grossPay * 0.25 : 0;
        const deductions = grossPay > 0 ? grossPay - lakefrontCut - netDue : 0;
        
        console.log(`Lakefront: Settlement ${i + 1} - Broker: ${broker}, Date: ${deliveryDate}, Gross: $${grossPay}, Net: $${netDue}`);
        
        settlements.push({
          id: crypto.randomUUID(),
          company: 'Lakefront',
          date: deliveryDate,
          controlNumber: `LF-${broker}-${deliveryDate || Date.now()}`,
          terminal: broker,
          origin: '',
          destination: '',
          miles: 0,
          lineHaul: grossPay - lakefrontCut,
          fuelSurcharge: 0,
          tolls: 0,
          otherPay: 0,
          totalPay: grossPay || netDue,
          deductions: Math.max(0, deductions),
          checkAmount: netDue,
          selected: true
        });
      }
      
      console.log(`Lakefront: Created ${settlements.length} settlements from pay breakdown`);
    }
    
    // Check for C.H. Robinson rate confirmations (common broker for Lakefront)
    const isCHRobinson = text.includes('C.H. Robinson') || text.includes('CH Robinson') || text.includes('CHRobinson');
    
    if (isCHRobinson && settlements.length === 0) {
      console.log('Lakefront: Detected C.H. Robinson Load Confirmation');
      
      // Extract Load # - multiple patterns:
      // "Load Confirmation - #315693768"
      // "Service for Load #315693768"
      // "Load Number QUICK PAY" after a load number
      const loadNumMatch = text.match(/(?:Service for |for )Load\s*#?\s*(\d{6,12})/i) ||
                          text.match(/Load Confirmation[^#]*#(\d{6,12})/i) ||
                          text.match(/Load\s*#\s*(\d{6,12})/i);
      const loadNumber = loadNumMatch ? loadNumMatch[1] : '';
      console.log('CHR Load #:', loadNumber);
      
      // Extract Line Haul from "Line Haul - FLAT RATE | 1 | $1,000.00 | $1,000.00" or "Line Haul ... $X,XXX.XX"
      // Look for dollar amount after "Line Haul"
      const lineHaulMatch = text.match(/Line Haul[^$\d]*[\$]?([\d,]+\.?\d{2})/i);
      const lineHaul = lineHaulMatch ? parseFloat(lineHaulMatch[1].replace(/,/g, '')) : 0;
      console.log('CHR Line Haul:', lineHaul);
      
      // Extract Total - look for "Total:" followed by dollar amount (may have spaces/underscores)
      const totalMatch = text.match(/Total:[\s_]*\$?([\d,]+\.?\d{2})/i);
      const totalRate = totalMatch ? parseFloat(totalMatch[1].replace(/,/g, '')) : lineHaul;
      console.log('CHR Total:', totalRate);
      
      // Extract Fuel Surcharge - "a $425.10 fuel surcharge" or "$425.10 fuel surcharge"
      const fuelMatch = text.match(/\$?([\d,]+\.\d{2})\s*fuel surcharge/i) ||
                       text.match(/fuel surcharge[^\d]*\$?([\d,]+\.\d{2})/i);
      const fuelSurcharge = fuelMatch ? parseFloat(fuelMatch[1].replace(/,/g, '')) : 0;
      console.log('CHR Fuel Surcharge:', fuelSurcharge);
      
      // Extract Pick Up Date - format like "2/5/20-2/7/20" or "2/5/20"
      const pickupDateMatch = text.match(/Pick\s*Up\s*Date[:\s]*[^0-9]*(\d{1,2}\/\d{1,2}\/\d{2,4})/i);
      let pickupDate = '';
      if (pickupDateMatch) {
        const parts = pickupDateMatch[1].split('/');
        if (parts.length >= 3) {
          const month = parts[0].padStart(2, '0');
          const day = parts[1].padStart(2, '0');
          let year = parts[2];
          if (year.length === 2) year = '20' + year;
          pickupDate = `${year}-${month}-${day}`;
        }
      }
      console.log('CHR Pickup Date:', pickupDate);
      
      // Extract Delivery Date
      const deliveryDateMatch = text.match(/Delivery\s*Date[:\s]*[^0-9]*(\d{1,2}\/\d{1,2}\/\d{2,4})/i);
      let deliveryDate = '';
      if (deliveryDateMatch) {
        const parts = deliveryDateMatch[1].split('/');
        if (parts.length >= 3) {
          const month = parts[0].padStart(2, '0');
          const day = parts[1].padStart(2, '0');
          let year = parts[2];
          if (year.length === 2) year = '20' + year;
          deliveryDate = `${year}-${month}-${day}`;
        }
      }
      console.log('CHR Delivery Date:', deliveryDate);
      
      // Extract Shipper info (origin)
      const shipperMatch = text.match(/SHIPPER[^:]*:[^\n]*?([A-Za-z][A-Za-z\s&]+)(?:\s*\||\s*Address)/i);
      const originAddressMatch = text.match(/SHIPPER[\s\S]*?Address[:\s]*([^\n|]+)/i);
      let origin = '';
      if (shipperMatch) {
        origin = shipperMatch[1].trim();
      }
      if (originAddressMatch) {
        // Try to extract city, state from address
        const cityStateMatch = originAddressMatch[1].match(/([A-Za-z\s]+),?\s*([A-Z]{2})\s*\d{5}/);
        if (cityStateMatch) {
          origin = `${cityStateMatch[1].trim()}, ${cityStateMatch[2]}`;
        }
      }
      
      // Extract Receiver info (destination)
      const receiverMatch = text.match(/RECEIVER[^:]*:[^\n]*?([A-Za-z][A-Za-z\s&]+)(?:\s*\||\s*Address)/i);
      const destAddressMatch = text.match(/RECEIVER[\s\S]*?Address[:\s]*([^\n|]+)/i);
      let destination = '';
      if (receiverMatch) {
        destination = receiverMatch[1].trim();
      }
      if (destAddressMatch) {
        const cityStateMatch = destAddressMatch[1].match(/([A-Za-z\s]+),?\s*([A-Z]{2})\s*\d{5}/);
        if (cityStateMatch) {
          destination = `${cityStateMatch[1].trim()}, ${cityStateMatch[2]}`;
        }
      }
      
      console.log('CHR Origin:', origin, 'Destination:', destination);
      
      // If no dates found, try to find ANY date in the text
      if (!pickupDate && !deliveryDate) {
        // Look for any date pattern like "2/5/20" or "02/05/2020"
        const anyDateMatch = text.match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
        if (anyDateMatch) {
          const month = anyDateMatch[1].padStart(2, '0');
          const day = anyDateMatch[2].padStart(2, '0');
          let year = anyDateMatch[3];
          if (year.length === 2) year = '20' + year;
          pickupDate = `${year}-${month}-${day}`;
          console.log('CHR Fallback date found:', pickupDate);
        }
      }
      
      // Final fallback - use current date if no date found at all
      const finalDate = deliveryDate || pickupDate || new Date().toISOString().split('T')[0];
      console.log('CHR Final date:', finalDate);
      
      if (totalRate > 0 || lineHaul > 0) {
        const grossPay = totalRate || lineHaul;
        // Lakefront takes 25%, plus insurance (~$350)
        const lakefrontCut = grossPay * 0.25;
        const estimatedInsurance = 350;
        const estimatedNet = grossPay - lakefrontCut - estimatedInsurance;
        
        console.log(`CHR Settlement: Load #${loadNumber}, Gross: $${grossPay}, Est Net: $${Math.max(0, estimatedNet)}`);
        
        settlements.push({
          id: crypto.randomUUID(),
          company: 'Lakefront',
          date: finalDate,
          controlNumber: `CHR-${loadNumber}`,
          terminal: 'CHR',
          origin,
          destination,
          miles: 0,
          lineHaul: grossPay - lakefrontCut,
          fuelSurcharge,
          tolls: 0,
          otherPay: 0,
          totalPay: grossPay,
          deductions: lakefrontCut + estimatedInsurance,
          checkAmount: Math.max(0, estimatedNet),
          selected: true
        });
      }
    }
    
    // Check for Destination Transport rate confirmations (another common broker for Lakefront hotshot)
    const isDestinationTransport = text.includes('Destination Transport') || 
                                   text.includes('DESTINATION TRANSPORT') ||
                                   text.includes('DestINATION') ||
                                   text.includes('destinationtrans.com');
    
    if (isDestinationTransport && settlements.length === 0) {
      console.log('Lakefront: Detected Destination Transport Load Confirmation');
      
      // Extract Load # - format: "Load Confirmation 9019542" or "Order: 9019542"
      const loadNumMatch = text.match(/Load Confirmation[\s\n]*(\d{6,10})/i) ||
                          text.match(/Order:\s*(\d{6,10})/i) ||
                          text.match(/(\d{7})/); // Just find a 7-digit number
      const loadNumber = loadNumMatch ? loadNumMatch[1] : '';
      console.log('Destination Load #:', loadNumber);
      
      // Extract Carrier Pay - look for $X,XXX.XX pattern near "Pay" or "Carrier"
      // Handle newlines between label and value
      let carrierPay = 0;
      const payPatterns = [
        /Carrier Freight Pay[\s\S]{0,20}\$?([\d,]+\.\d{2})/i,
        /Total Carrier Pay[\s\S]{0,20}\$?([\d,]+\.\d{2})/i,
        /Carrier Pay[\s\S]{0,20}\$?([\d,]+\.\d{2})/i,
        /\$(\d{1,3},\d{3}\.\d{2})/ // Just find $X,XXX.XX format
      ];
      for (const pattern of payPatterns) {
        const match = text.match(pattern);
        if (match) {
          carrierPay = parseFloat(match[1].replace(/,/g, ''));
          if (carrierPay > 0) break;
        }
      }
      console.log('Destination Carrier Pay:', carrierPay);
      
      // Extract Miles - handle various formats
      const milesMatch = text.match(/Miles:\s*([\d,]+\.?\d*)/i) ||
                        text.match(/(\d{3,4})\.0\b/); // Find XXX.0 or XXXX.0 pattern
      const miles = milesMatch ? parseFloat(milesMatch[1].replace(/,/g, '')) : 0;
      console.log('Destination Miles:', miles);
      
      // Extract Pickup Date - handles both "04/01/2020" and garbled "04/0112020" formats
      const pickupDateMatch = text.match(/PU\s*\d*[\s\S]*?(\d{1,2}\/\d{1,2}\/?\d{2,4})/i) ||
                             text.match(/Pick.*?(\d{1,2}\/\d{1,2}\/?\d{2,4})/i);
      let pickupDate = '';
      if (pickupDateMatch) {
        let dateStr = pickupDateMatch[1];
        // Handle garbled format like "04/0112020" -> "04/01/2020"
        const garbledMatch = dateStr.match(/(\d{1,2})\/(\d{2})(\d{4})/);
        if (garbledMatch) {
          const month = garbledMatch[1].padStart(2, '0');
          const day = garbledMatch[2];
          const year = garbledMatch[3];
          pickupDate = `${year}-${month}-${day}`;
        } else {
          const parts = dateStr.split('/');
          if (parts.length >= 3) {
            const month = parts[0].padStart(2, '0');
            const day = parts[1].padStart(2, '0');
            let year = parts[2];
            if (year.length === 2) year = '20' + year;
            pickupDate = `${year}-${month}-${day}`;
          }
        }
      }
      console.log('Destination Pickup Date:', pickupDate);
      
      // Extract Delivery Date - handles both "04/03/2020" and garbled "04/0312020" formats
      const deliveryDateMatch = text.match(/SO\s*\d*[\s\S]*?(\d{1,2}\/\d{1,2}\/?\d{2,4})/i) ||
                               text.match(/Delivery.*?(\d{1,2}\/\d{1,2}\/?\d{2,4})/i);
      let deliveryDate = '';
      if (deliveryDateMatch) {
        let dateStr = deliveryDateMatch[1];
        // Handle garbled format like "04/0312020" -> "04/03/2020"
        const garbledMatch = dateStr.match(/(\d{1,2})\/(\d{2})(\d{4})/);
        if (garbledMatch) {
          const month = garbledMatch[1].padStart(2, '0');
          const day = garbledMatch[2];
          const year = garbledMatch[3];
          deliveryDate = `${year}-${month}-${day}`;
        } else {
          const parts = dateStr.split('/');
          if (parts.length >= 3) {
            const month = parts[0].padStart(2, '0');
            const day = parts[1].padStart(2, '0');
            let year = parts[2];
            if (year.length === 2) year = '20' + year;
            deliveryDate = `${year}-${month}-${day}`;
          }
        }
      }
      console.log('Destination Delivery Date:', deliveryDate);
      
      // Extract Origin - look for SPANISH FORT AL pattern or similar
      // Handle garbled state codes like "Ml" instead of "MI"
      const originMatch = text.match(/(?:PU|SPANISH|Address)[^A-Z]*([A-Z][A-Z\s]+)\s+([A-Z][A-Za-z])\s+(\d{5})/i) ||
                         text.match(/SPANISH\s+FORT\s+([A-Z]{2})/i);
      let origin = '';
      if (originMatch) {
        if (originMatch[3]) {
          // Full match with zip
          let state = originMatch[2].toUpperCase();
          if (state === 'ML' || state === 'Ml') state = 'MI'; // Common OCR error
          origin = `${originMatch[1].trim()}, ${state}`;
        } else {
          origin = `Spanish Fort, ${originMatch[1]}`;
        }
      }
      // Fallback: look for "SPANISH FORT" specifically
      if (!origin && text.includes('SPANISH FORT')) {
        origin = 'Spanish Fort, AL';
      }
      console.log('Destination Origin:', origin);
      
      // Extract Destination - look for EAST CHINA MI pattern
      const destMatch = text.match(/(?:SO|EAST\s+CHINA|River\s+Rd)[^A-Z]*([A-Z][A-Z\s]+)\s+([A-Z][A-Za-z])\s+(\d{5})/i) ||
                       text.match(/EAST\s+CHINA\s+([A-Z]{2}|Ml)/i);
      let destination = '';
      if (destMatch) {
        if (destMatch[3]) {
          let state = destMatch[2].toUpperCase();
          if (state === 'ML' || state === 'Ml') state = 'MI';
          destination = `${destMatch[1].trim()}, ${state}`;
        } else {
          let state = destMatch[1].toUpperCase();
          if (state === 'ML' || state === 'Ml') state = 'MI';
          destination = `East China, ${state}`;
        }
      }
      // Fallback: look for "EAST CHINA" specifically  
      if (!destination && text.includes('EAST CHINA')) {
        destination = 'East China, MI';
      }
      console.log('Destination Dest:', destination);
      
      // Try additional date extraction if still missing
      if (!pickupDate && !deliveryDate) {
        // Look for any date pattern MM/DD/YYYY or M/D/YY
        const anyDateMatch = text.match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
        if (anyDateMatch) {
          const month = anyDateMatch[1].padStart(2, '0');
          const day = anyDateMatch[2].padStart(2, '0');
          let year = anyDateMatch[3];
          if (year.length === 2) year = '20' + year;
          pickupDate = `${year}-${month}-${day}`;
          console.log('Destination Fallback date:', pickupDate);
        }
      }
      
      // Final fallback for date
      const finalDate = deliveryDate || pickupDate || new Date().toISOString().split('T')[0];
      
      // Even if pay is 0, try to extract it one more way
      if (carrierPay === 0) {
        // Look for "2,000.00" or "2000.00" patterns
        const amountMatch = text.match(/(\d{1,3},?\d{3}\.\d{2})/);
        if (amountMatch) {
          carrierPay = parseFloat(amountMatch[1].replace(/,/g, ''));
          console.log('Destination Fallback Pay:', carrierPay);
        }
      }
      
      if (carrierPay > 0) {
        // Lakefront takes 25%, plus insurance (~$350)
        const lakefrontCut = carrierPay * 0.25;
        const estimatedInsurance = 350;
        const estimatedNet = carrierPay - lakefrontCut - estimatedInsurance;
        
        console.log(`Destination Settlement: Load #${loadNumber}, Pay: $${carrierPay}, Miles: ${miles}, Est Net: $${Math.max(0, estimatedNet)}`);
        
        settlements.push({
          id: crypto.randomUUID(),
          company: 'Lakefront',
          date: finalDate,
          controlNumber: `DEST-${loadNumber || Date.now()}`,
          terminal: 'Destination',
          origin: origin || 'Spanish Fort, AL',
          destination: destination || 'East China, MI',
          miles: miles || 1021,
          lineHaul: carrierPay - lakefrontCut,
          fuelSurcharge: 0,
          tolls: 0,
          otherPay: 0,
          totalPay: carrierPay,
          deductions: lakefrontCut + estimatedInsurance,
          checkAmount: Math.max(0, estimatedNet),
          selected: true
        });
      }
    }
    
    // Also check for Rate Confirmation format (fallback for when no Pay Breakdown exists)
    // Rate Con has: Line Haul rate, Pick-Up/Delivery locations and dates
    const isRateCon = text.includes('Rate Confirmation') || 
                     text.includes('Rate:') ||
                     text.includes('Line Haul') ||
                     text.includes('Total Rate:');
    
    if (isRateCon && settlements.length === 0) {
      console.log('Lakefront: Detected Rate Confirmation format');
      
      // Split by rate confirmation sections
      const sections = text.split(/(?=(?:Carrier's Rate Confirmation|Rate Confirmation|Rate & Load Confirmation))/i);
      
      for (const section of sections) {
        if (!section.includes('Lakefront') && !section.includes('LAKEFRONT')) continue;
        
        // Extract Line Haul / Total Rate
        const rateMatch = section.match(/(?:Total Rate|Rate|Line Haul)[:\s]*\$?([\d,]+\.?\d*)/i);
        const lineHaul = rateMatch ? parseFloat(rateMatch[1].replace(/,/g, '')) : 0;
        
        if (lineHaul === 0) continue;
        
        // Extract pickup date and location
        const pickupMatch = section.match(/(?:Pick-?Up|Ship Date|Pickup)[:\s]*.*?(\d{1,2}\/\d{1,2}\/\d{2,4})/i) ||
                           section.match(/Date:\s*.*?(\d{1,2}\/\d{1,2}\/\d{2,4})/i);
        let pickupDate = '';
        if (pickupMatch) {
          const parts = pickupMatch[1].split('/');
          if (parts.length >= 2) {
            const month = parts[0].padStart(2, '0');
            const day = parts[1].padStart(2, '0');
            let year = parts[2] || new Date().getFullYear().toString();
            if (year.length === 2) year = '20' + year;
            pickupDate = `${year}-${month}-${day}`;
          }
        }
        
        // Extract delivery date
        const deliveryMatch = section.match(/(?:Delivery|Dropoff)[:\s]*.*?(\d{1,2}\/\d{1,2}\/\d{2,4})/i);
        let deliveryDate = '';
        if (deliveryMatch) {
          const parts = deliveryMatch[1].split('/');
          if (parts.length >= 2) {
            const month = parts[0].padStart(2, '0');
            const day = parts[1].padStart(2, '0');
            let year = parts[2] || new Date().getFullYear().toString();
            if (year.length === 2) year = '20' + year;
            deliveryDate = `${year}-${month}-${day}`;
          }
        }
        
        // Extract origin/destination
        const originMatch = section.match(/(?:Pick-?Up|Ship\s*From)[^A-Z]*([A-Z][A-Za-z\s]+,?\s*[A-Z]{2})/i);
        const destMatch = section.match(/(?:Delivery|Ship\s*To|Dropoff)[^A-Z]*([A-Z][A-Za-z\s]+,?\s*[A-Z]{2})/i);
        const origin = originMatch ? originMatch[1].trim() : '';
        const destination = destMatch ? destMatch[1].trim() : '';
        
        // Extract broker name
        const brokerMatch = section.match(/(?:Armstrong|A-1|Brock|Wide Range|WFX|CHR)/i);
        const broker = brokerMatch ? brokerMatch[0] : 'Unknown';
        
        // Extract Load/PRO number
        const loadMatch = section.match(/(?:Load\s*#|PRO\s*#|Load ID)[:\s]*(\d+|[A-Z0-9-]+)/i);
        const loadNumber = loadMatch ? loadMatch[1] : `RC-${pickupDate}`;
        
        // Estimate net pay (gross - 25% Lakefront cut - ~$350 insurance)
        const estimatedCut = lineHaul * 0.25;
        const estimatedInsurance = 350;
        const estimatedNet = lineHaul - estimatedCut - estimatedInsurance;
        
        // Final fallback for date
        const finalDate = deliveryDate || pickupDate || new Date().toISOString().split('T')[0];
        
        console.log(`Lakefront RateCon: ${broker} - ${origin} to ${destination}, Rate: $${lineHaul}, Est Net: $${estimatedNet}`);
        
        settlements.push({
          id: crypto.randomUUID(),
          company: 'Lakefront',
          date: finalDate,
          controlNumber: loadNumber,
          terminal: broker,
          origin,
          destination,
          miles: 0, // Could estimate from origin/destination
          lineHaul,
          fuelSurcharge: 0,
          tolls: 0,
          otherPay: 0,
          totalPay: lineHaul,
          deductions: estimatedCut + estimatedInsurance,
          checkAmount: Math.max(0, estimatedNet),
          selected: true
        });
      }
    }
    
    console.log(`Lakefront: Parsed ${settlements.length} settlements`);
    return settlements;
  };

  // Main parser - detects company and routes to appropriate parser
  const parseSettlementText = (text: string): ParsedSettlement[] => {
    const settlements: ParsedSettlement[] = [];
    
    // Check for Lakefront Transport first (Hotshot - Pay Breakdown Sheets and Rate Confirmations)
    // Also check for C.H. Robinson and Destination Transport docs that reference Lakefront as the carrier
    const isLakefront = text.includes('Lakefront') || text.includes('LAKEFRONT') || text.includes('LAkefront') || 
                        text.includes('986939') || text.includes('lakefronttransport') ||
                        text.includes('C.H. Robinson') || text.includes('CH Robinson') || // CHR broker
                        text.includes('Destination Transport') || text.includes('DESTINATION TRANSPORT') || // Destination broker
                        text.includes('DestINATION') || text.includes('destinationtrans.com');
    
    if (isLakefront) {
      console.log('Main: Checking for Lakefront settlements...');
      const lakefrontSettlements = parseLakefront(text);
      if (lakefrontSettlements.length > 0) {
        console.log(`Main: Found ${lakefrontSettlements.length} Lakefront settlements`);
        settlements.push(...lakefrontSettlements);
        return settlements;
      }
    }
    
    // Check for Auto Driveaway "Purchase by Vendor Detail" summary report FIRST
    // This format has multiple line items per BOL that need to be grouped
    if (text.includes('Auto Driveaway') || text.includes('Purchase by Vendor Detail') || text.includes('Vehicle Transport')) {
      console.log('Main: Checking for Auto Driveaway summary report...');
      const summarySettlements = parseAutoDriveawaySummaryReport(text);
      if (summarySettlements.length > 0) {
        console.log(`Main: Found ${summarySettlements.length} settlements in Auto Driveaway summary report`);
        settlements.push(...summarySettlements);
        return settlements; // Summary report is comprehensive, no need to continue parsing
      }
    }
    
    // Check for Coldiron format (Remittance Advice)
    if (text.includes('Coldiron') || text.includes('Remittance Advice') || text.includes('Driver Card')) {
      console.log('Main: Checking for Coldiron settlements...');
      // Split by "Remittance Advice" to get individual settlements
      const coldiSections = text.split(/(?=Remittance Advice)/i).filter(s => s.trim() && s.length > 100);
      console.log('Main: Found', coldiSections.length, 'potential Coldiron sections');
      for (const section of coldiSections) {
        if (section.includes('Driver Card') || section.includes('Coldiron') || section.includes('Total for Payment')) {
          const settlement = parseColdiron(section);
          if (settlement) {
            const exists = settlements.some(s => s.controlNumber === settlement.controlNumber);
            if (!exists) {
              console.log('Main: Added Coldiron settlement:', settlement.controlNumber);
              settlements.push(settlement);
            }
          }
        }
      }
    }
    
    // Check for Dynamic Transport format (Travel Trailer transport from Goshen, IN)
    // Patterns: "DYNAMIC DISSEMINATION", "DYNAMIC BENCHMARKS", Goshen IN address, or phone 574-333-3042
    const hasDynamicIndicators = text.includes('DYNAMIC') || 
                                  text.includes('Dynamic') ||
                                  text.includes('Goshen, IN 46526') ||
                                  text.includes('574-333-3042') ||
                                  text.includes('2408 Eisenhower') ||
                                  (text.includes('Driver Settlement') && text.includes('Linehaul') && text.includes('Travel Trailer'));
    
    if (hasDynamicIndicators) {
      console.log('Main: Checking for Dynamic Transport settlements...');
      
      // Split by "Driver Settlement" or "DYNAMIC" headers or Date: patterns
      const dynamicSections = text.split(/(?=DYNAMIC\s+(?:DISSEMINATION|BENCHMARKS))|(?=Driver Settlement\s+Date:)|(?=Date:\s+\d{1,2}\/\d{1,2}\/\d{4}\s+2408)/i)
        .filter(s => s.trim() && s.length > 100);
      
      console.log('Main: Found', dynamicSections.length, 'potential Dynamic sections');
      
      // If only one section, try the whole text
      if (dynamicSections.length <= 1) {
        const settlement = parseDynamic(text);
        if (settlement) {
          console.log('Main: Added Dynamic settlement (single):', settlement.controlNumber);
          settlements.push(settlement);
        }
      } else {
        for (const section of dynamicSections) {
          const settlement = parseDynamic(section);
          if (settlement) {
            const exists = settlements.some(s => s.controlNumber === settlement.controlNumber);
            if (!exists) {
              console.log('Main: Added Dynamic settlement:', settlement.controlNumber, '-', settlement.destination);
              settlements.push(settlement);
            }
          }
        }
      }
      
      // If we found Dynamic settlements, we're done
      if (settlements.length > 0) {
        console.log(`Main: Found ${settlements.length} Dynamic settlements`);
        return settlements;
      }
    }
    
    // Check for Quality Driveaway Pay Detail format first (from portal)
    if (text.includes('Pay Detail') && text.includes('Driver #')) {
      console.log('Main: Checking for Quality Pay Detail format...');
      // Try parsing the whole text as one Pay Detail
      const settlement = parseQualityPayDetail(text);
      if (settlement) {
        const exists = settlements.some(s => s.controlNumber === settlement.controlNumber);
        if (!exists) {
          console.log('Main: Added Quality PayDetail settlement:', settlement.controlNumber);
          settlements.push(settlement);
        }
      }
    }
    
    // Check for Quality Driveaway format (Driver # with Taxable Earnings)
    if (text.includes('Driver #') && (text.includes('Taxable Earnings') || text.includes('Net Settlement')) && !text.includes('Pay Detail')) {
      console.log('Main: Checking for Quality Driveaway settlements...');
      // Split by "Net Settlement" to separate individual settlements, or by the header pattern
      const qdSections = text.split(/(?=DOUGLAS SULLIVAN Driver #)|(?=Net Settlement[^$]*\$[\d.]+\s*$)/im).filter(s => s.trim() && s.length > 50);
      console.log('Main: Found', qdSections.length, 'potential Quality sections');
      
      // Also try splitting by order number pattern
      if (qdSections.length <= 1) {
        const altSections = text.split(/(?=\d{6}\s+\d{1,2}\/\d{1,2}\/\d{4})/i).filter(s => s.trim() && s.length > 50);
        if (altSections.length > 1) {
          for (const section of altSections) {
            // Need to include Driver # context
            const fullSection = text.includes('Driver #') ? `Driver #35745\n${section}` : section;
            const settlement = parseQualityDriveaway(fullSection);
            if (settlement) {
              const exists = settlements.some(s => s.controlNumber === settlement.controlNumber);
              if (!exists) {
                console.log('Main: Added Quality settlement:', settlement.controlNumber);
                settlements.push(settlement);
              }
            }
          }
        }
      } else {
        for (const section of qdSections) {
          const settlement = parseQualityDriveaway(section);
          if (settlement) {
            const exists = settlements.some(s => s.controlNumber === settlement.controlNumber);
            if (!exists) {
              console.log('Main: Added Quality settlement:', settlement.controlNumber);
              settlements.push(settlement);
            }
          }
        }
      }
    }
    
    // Check for Auto Driveaway format
    if (text.includes('Auto Driveaway') || text.match(/\d{3}-\d{6}-\d{3}/)) {
      // Split by BOL patterns for multiple Auto Driveaway settlements
      const adSections = text.split(/(?=\d{1,2}\/\d{1,2}\/\d{4}\s+DOUG)/i);
      for (const section of adSections) {
        const settlement = parseAutoDriveawaySettlement(section);
        if (settlement) {
          const exists = settlements.some(s => s.controlNumber === settlement.controlNumber);
          if (!exists) settlements.push(settlement);
        }
      }
    }
    
    // Check for Horizon Transport format
    const horizonSettlements = parseHorizonYTD(text);
    if (horizonSettlements.length > 0) {
      console.log('Main: Detected Horizon Transport format');
      for (const settlement of horizonSettlements) {
        const exists = settlements.some(s => s.controlNumber === settlement.controlNumber && s.date === settlement.date);
        if (!exists) {
          settlements.push(settlement);
        }
      }
      console.log('Main: Added', horizonSettlements.length, 'settlements from Horizon');
    }
    
    // Check for Bennett Year-to-Date format FIRST (before regular Bennett)
    if (text.includes('SETTLEMENTS HISTORY LISTING') || text.includes('SETLSTH')) {
      console.log('Main: Detected Bennett Year-to-Date format');
      const ytdSettlements = parseBennettYTD(text);
      if (ytdSettlements.length > 0) {
        for (const settlement of ytdSettlements) {
          const exists = settlements.some(s => s.controlNumber === settlement.controlNumber);
          if (!exists) {
            settlements.push(settlement);
          }
        }
        console.log('Main: Added', ytdSettlements.length, 'settlements from Bennett YTD');
      }
    }
    
    // Check for Bennett format (SETTLEMENT PAY, CONTROL #, LINEHAUL, etc.)
    // Try to parse as one complete document first
    const hasBennettKeywords = text.includes('SETTLEMENT PAY') || 
                               text.includes('SETTLEMENTS STATEMENT') ||
                               text.includes('CONTROL #') || 
                               text.includes('Statement#') ||
                               text.includes('BENNETT') || 
                               text.includes('SENNETT') ||
                               text.includes('LINEHAUL') ||
                               text.includes('LINE HAUL') ||
                               text.includes('TOTAL PAY BEFORE DEDUCTIONS') ||
                               text.includes('BALANCE TO BE PAID') ||
                               text.includes('DRIVER RELIEF FUND') ||
                               text.includes('STMT TOTAL');
    
    if (hasBennettKeywords && settlements.length === 0) {
      console.log('Main: Checking for Bennett settlements...');
      
      // First try parsing the whole thing as one settlement
      const singleSettlement = parseBennettSettlement(text);
      if (singleSettlement && !settlements.some(s => s.controlNumber === singleSettlement.controlNumber)) {
        console.log('Main: Added single Bennett settlement:', singleSettlement.controlNumber);
        settlements.push(singleSettlement);
      }
      
      // Also try splitting for multiple settlements - handle both old and new formats
      // New: "SETTLEMENT PAY" Old: "SETTLEMENTS STATEMENT"
      const bennettSections = text.split(/(?=SETTLEMENT PAY)|(?=SETTLEMENTS STATEMENT)|(?=CONTROL\s*#[:\s]*\d)|(?=Statement\s*#[:\s]*\d)/i).filter(s => s.trim() && s.length > 100);
      console.log('Main: Found', bennettSections.length, 'potential Bennett sections');
      
      for (const section of bennettSections) {
        const settlement = parseBennettSettlement(section);
        if (settlement) {
          // Avoid duplicates
          const exists = settlements.some(s => s.controlNumber === settlement.controlNumber);
          if (!exists) {
            console.log('Main: Added Bennett settlement:', settlement.controlNumber);
            settlements.push(settlement);
          }
        }
      }
    }
    
    // Generic fallback - look for common settlement patterns
    if (settlements.length === 0) {
      // Try to find any settlement-like data
      const genericMatch = text.match(/(?:settlement|total pay|check amount)[:\s]*\$?([\d,]+\.?\d*)/gi);
      if (genericMatch && genericMatch.length > 0) {
        // Found potential settlement amounts, try basic parsing
        const dateMatch = text.match(/(\d{1,2}\/\d{1,2}\/\d{2,4})/);
        const amountMatch = text.match(/(?:total|settlement|pay)[:\s]*\$?([\d,]+\.?\d*)/i);
        
        if (amountMatch) {
          const amount = parseFloat(amountMatch[1].replace(/,/g, ''));
          if (amount > 0) {
            let formattedDate = '';
            if (dateMatch) {
              const parts = dateMatch[1].split('/');
              const month = parts[0].padStart(2, '0');
              const day = parts[1].padStart(2, '0');
              let year = parts[2];
              if (year.length === 2) year = '20' + year;
              formattedDate = `${year}-${month}-${day}`;
            }
            
            settlements.push({
              id: crypto.randomUUID(),
              company: 'Other',
              date: formattedDate,
              controlNumber: `MANUAL-${Date.now()}`,
              terminal: 'N/A',
              origin: '',
              destination: '',
              miles: 0,
              lineHaul: amount,
              fuelSurcharge: 0,
              tolls: 0,
              otherPay: 0,
              totalPay: amount,
              deductions: 0,
              checkAmount: amount,
              selected: true
            });
          }
        }
      }
    }
    
    return settlements;
  };

  // Extract text from PDF
  const extractTextFromPDF = async (file: File): Promise<string> => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    
    let fullText = '';
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = textContent.items
        .map((item: any) => item.str)
        .join(' ');
      fullText += pageText + '\n\n';
    }
    
    return fullText;
  };

  // Store the original uploaded file
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  // Handle file drop
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;
    
    setIsLoading(true);
    setError(null);
    setImportStatus(null);
    setUploadedFile(file); // Store the original file!
    
    try {
      const text = await extractTextFromPDF(file);
      setPdfText(text);
      
      const settlements = parseSettlementText(text);
      if (settlements.length === 0) {
        setError('No settlements found in PDF. Try pasting the text manually below.');
      } else {
        setParsedSettlements(settlements);
      }
    } catch (e: any) {
      console.error('PDF extraction error:', e);
      setError(`Failed to read PDF: ${e.message}. Try pasting the text manually.`);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    maxFiles: 1
  });

  // Parse from pasted text
  const handleParseText = () => {
    if (!pdfText.trim()) return;
    
    setError(null);
    const settlements = parseSettlementText(pdfText);
    if (settlements.length === 0) {
      setError('No settlements found in the text. Make sure you copied the full settlement content.');
    } else {
      setParsedSettlements(settlements);
    }
  };

  // Toggle settlement selection
  const toggleSelection = (id: string) => {
    setParsedSettlements(prev => 
      prev.map(s => s.id === id ? { ...s, selected: !s.selected } : s)
    );
  };

  // Remove settlement from list
  const removeSettlement = (id: string) => {
    setParsedSettlements(prev => prev.filter(s => s.id !== id));
  };

  // Get company badge color
  const getCompanyColor = (company: string) => {
    switch (company) {
      case 'Bennett': return 'bg-blue-500/20 text-blue-400 border-blue-500/50';
      case 'Auto Driveaway': return 'bg-purple-500/20 text-purple-400 border-purple-500/50';
      case 'Coldiron': return 'bg-orange-500/20 text-orange-400 border-orange-500/50';
      case 'Quality Driveaway': return 'bg-green-500/20 text-green-400 border-green-500/50';
      case 'Dynamic': return 'bg-cyan-500/20 text-cyan-400 border-cyan-500/50';
      case 'Horizon': return 'bg-amber-500/20 text-amber-400 border-amber-500/50';
      case 'Lakefront': return 'bg-teal-500/20 text-teal-400 border-teal-500/50';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/50';
    }
  };

  // Handle manual settlement entry (create or edit)
  const handleManualEntry = async () => {
    if (!manualEntry.date || !manualEntry.grossPay) {
      setError('Date and Gross Pay are required');
      return;
    }
    
    const grossPay = parseFloat(manualEntry.grossPay) || 0;
    const netPay = manualEntry.netPay ? parseFloat(manualEntry.netPay) : grossPay * 0.75 - 350; // Default: 25% cut + $350 insurance
    const miles = parseFloat(manualEntry.miles) || 0;
    
    const settlement: ParsedSettlement = {
      id: editingSettlementId || crypto.randomUUID(),
      company: manualEntry.company,
      date: manualEntry.date,
      controlNumber: manualEntry.controlNumber || `MANUAL-${Date.now()}`,
      terminal: editingSettlementId ? 'Edited' : 'Manual Entry',
      origin: manualEntry.origin || '',
      destination: manualEntry.destination || '',
      miles,
      lineHaul: netPay,
      fuelSurcharge: 0,
      tolls: 0,
      otherPay: 0,
      totalPay: grossPay,
      deductions: grossPay - netPay,
      checkAmount: netPay,
      selected: true
    };
    
    if (editingSettlementId) {
      // Update existing settlement
      setParsedSettlements(prev => prev.map(s => s.id === editingSettlementId ? settlement : s));
    } else {
      // Add new settlement
      setParsedSettlements(prev => [...prev, settlement]);
    }
    
    // Clear form and hide it
    setManualEntry({
      company: 'Lakefront',
      date: '',
      controlNumber: '',
      origin: '',
      destination: '',
      miles: '',
      grossPay: '',
      netPay: ''
    });
    setEditingSettlementId(null);
    setShowManualEntry(false);
    setError(null);
  };

  // Edit an existing settlement
  const handleEditSettlement = (settlement: ParsedSettlement) => {
    setManualEntry({
      company: settlement.company,
      date: settlement.date,
      controlNumber: settlement.controlNumber,
      origin: settlement.origin,
      destination: settlement.destination,
      miles: settlement.miles.toString(),
      grossPay: settlement.totalPay.toString(),
      netPay: settlement.checkAmount.toString()
    });
    setEditingSettlementId(settlement.id);
    setShowManualEntry(true);
  };

  // Bulk import Lakefront 2020 summary data
  const handleBulkImportLakefront2020 = () => {
    const lakefront2020Data = [
      { broker: 'CHR', pickup: '2020-01-07', delivery: '2020-01-10', gross: 1752, net: 1314, origin: 'Various', destination: 'Various', miles: 0 },
      { broker: 'RR', pickup: '2020-01-12', delivery: '2020-01-14', gross: 1300, net: 975, origin: 'Various', destination: 'Various', miles: 0 },
      { broker: 'CHR', pickup: '2020-01-17', delivery: '2020-01-22', gross: 2900, net: 2175, origin: 'Various', destination: 'Various', miles: 0 },
      { broker: 'Brock', pickup: '2020-01-28', delivery: '2020-01-30', gross: 2000, net: 1500, origin: 'Various', destination: 'Various', miles: 0 },
      { broker: 'JB Hunt', pickup: '2020-02-18', delivery: '2020-02-21', gross: 3000, net: 2250, origin: 'Bensalem, PA', destination: 'Herlong, CA', miles: 2697, controlNumber: 'TP87587' },
      { broker: 'Open Road', pickup: '2020-02-05', delivery: '2020-02-11', gross: 4800, net: 3600, origin: 'Various', destination: 'Various', miles: 0 },
      { broker: 'Nationwide', pickup: '2020-02-18', delivery: '2020-02-21', gross: 1100, net: 825, origin: 'Various', destination: 'Various', miles: 0 },
      { broker: 'King of Freight', pickup: '2020-03-05', delivery: '2020-03-09', gross: 2300, net: 1725, origin: 'Various', destination: 'Various', miles: 0 },
      { broker: 'GL Logistics', pickup: '2020-03-11', delivery: '2020-03-13', gross: 500, net: 375, origin: 'Various', destination: 'Various', miles: 0 },
      { broker: 'Logistics Plus', pickup: '2020-03-13', delivery: '2020-03-18', gross: 1400, net: 1050, origin: 'Various', destination: 'Various', miles: 0 },
      { broker: 'Trusco', pickup: '2020-03-17', delivery: '2020-03-18', gross: 800, net: 600, origin: 'Various', destination: 'Various', miles: 0 },
      { broker: 'Landstar', pickup: '2020-03-19', delivery: '2020-03-23', gross: 2200, net: 1650, origin: 'Various', destination: 'Various', miles: 0 },
      { broker: 'TQL', pickup: '2020-03-23', delivery: '2020-03-25', gross: 1400, net: 1050, origin: 'Various', destination: 'Various', miles: 0 },
      { broker: 'Destination', pickup: '2020-04-01', delivery: '2020-04-03', gross: 2000, net: 1500, origin: 'Spanish Fort, AL', destination: 'East China, MI', miles: 1021, controlNumber: '9019542' },
      { broker: 'Trinity', pickup: '2020-04-07', delivery: '2020-04-10', gross: 2100, net: 1575, origin: 'Various', destination: 'Various', miles: 0 },
      { broker: 'Express Logistics', pickup: '2020-05-08', delivery: '2020-05-11', gross: 1400, net: 1050, origin: 'Salinas, CA', destination: 'Clatskanie, OR', miles: 700, controlNumber: 'EXP5996016' },
      { broker: 'Specialized Transfer', pickup: '2020-05-14', delivery: '2020-05-18', gross: 1900, net: 1425, origin: 'Various', destination: 'Various', miles: 0 },
      { broker: 'Nimble', pickup: '2020-05-15', delivery: '2020-05-18', gross: 1900, net: 1425, origin: 'Various', destination: 'Various', miles: 0 },
      { broker: 'Wide Range', pickup: '2020-05-27', delivery: '2020-05-28', gross: 1700, net: 1275, origin: 'Various', destination: 'Various', miles: 0 },
      { broker: 'WFX Logistics', pickup: '2020-05-28', delivery: '2020-05-29', gross: 500, net: 375, origin: 'Various', destination: 'Various', miles: 0 },
      { broker: 'Unicron', pickup: '2020-05-29', delivery: '2020-06-01', gross: 1600, net: 1200, origin: 'Various', destination: 'Various', miles: 0 },
      { broker: 'GlobalTranz', pickup: '2020-06-03', delivery: '2020-06-05', gross: 1600, net: 1200, origin: 'Various', destination: 'Various', miles: 0 },
      { broker: 'Trident Transport', pickup: '2020-06-11', delivery: '2020-06-12', gross: 1450, net: 1087.50, origin: 'Various', destination: 'Various', miles: 0 },
    ];

    const newSettlements: ParsedSettlement[] = lakefront2020Data.map((load, index) => ({
      id: crypto.randomUUID(),
      company: 'Lakefront' as CompanyType,
      date: load.delivery,
      controlNumber: load.controlNumber || `LF-${load.broker.replace(/\s+/g, '')}-${load.delivery}`,
      terminal: load.broker,
      origin: load.origin,
      destination: load.destination,
      miles: load.miles,
      lineHaul: load.net,
      fuelSurcharge: 0,
      tolls: 0,
      otherPay: 0,
      totalPay: load.gross,
      deductions: load.gross - load.net,
      checkAmount: load.net,
      selected: true
    }));

    setParsedSettlements(prev => [...prev, ...newSettlements]);
    setError(null);
  };

  // Bulk import Dynamic 2020 settlements (Towaway - Travel Trailers)
  const handleBulkImportDynamic2020 = () => {
    // All 18 Dynamic settlements from 2020 binder - Total: $25,933.87 (1099: $26,259.49)
    const dynamic2020Data = [
      // September 2020 (5 trips) - $5,315.47
      { bol: '7216', date: '2020-09-15', miles: 191, lineHaul: 318.97, total: 299.97, origin: 'Elkhart, IN', destination: 'Couch\'s - Hamilton, OH' },
      { bol: '6847', date: '2020-09-17', miles: 574, lineHaul: 757.68, total: 706.81, origin: 'Elkhart, IN', destination: 'American Family - Salem, VA' },
      { bol: '7043', date: '2020-09-21', miles: 1010, lineHaul: 1686.70, total: 1564.33, origin: 'Elkhart, IN', destination: 'Fun Town - Denton, TX' },
      { bol: '7208', date: '2020-09-24', miles: 940, lineHaul: 1240.80, total: 1150.72, origin: 'Elkhart, IN', destination: 'RV Station - Colbert, OK' },
      { bol: '7419', date: '2020-09-29', miles: 1013, lineHaul: 1691.71, total: 1593.64, origin: 'Elkhart, IN', destination: 'Fun Town - Thackerville, OK' },
      // October 2020 (5 trips) - $5,748.03
      { bol: '7658', date: '2020-10-02', miles: 574, lineHaul: 757.68, total: 711.81, origin: 'Elkhart, IN', destination: 'American Family - Salem, VA' },
      { bol: '7792', date: '2020-10-05', miles: 782, lineHaul: 1032.24, total: 962.62, origin: 'Elkhart, IN', destination: 'American Family - Chesapeake, VA' },
      { bol: '7434-2', date: '2020-10-14', miles: 1182, lineHaul: 1560.24, total: 1499.22, origin: 'Elkhart, IN', destination: 'Windish - Fountain, CO' },
      { bol: '8289', date: '2020-10-19', miles: 574, lineHaul: 757.68, total: 706.81, origin: 'Elkhart, IN', destination: 'American Family - Salem, VA' },
      { bol: '8435', date: '2020-10-22', miles: 1157, lineHaul: 1932.19, total: 1867.57, origin: 'Elkhart, IN', destination: 'Demontrond - Houston, TX' },
      // November 2020 (3 trips) - $7,947.31
      { bol: 'FT165906', date: '2020-11-04', miles: 488, lineHaul: 697.84, total: 659.96, origin: 'Cleburne, TX', destination: 'Magee, MS' },
      { bol: '9149', date: '2020-11-10', miles: 2222, lineHaul: 3710.74, total: 3603.17, origin: 'Elkhart, IN', destination: 'Best - Turlock, CA' },
      { bol: '9426', date: '2020-11-23', miles: 2222, lineHaul: 3666.30, total: 3684.18, origin: 'Elkhart, IN', destination: 'Best - Turlock, CA' },
      // December 2020 (5 trips) - $6,923.06
      { bol: '9733', date: '2020-12-01', miles: 782, lineHaul: 1032.24, total: 957.62, origin: 'Elkhart, IN', destination: 'American Family - Chesapeake, VA' },
      { bol: '10281', date: '2020-12-08', miles: 1222, lineHaul: 2016.30, total: 1877.47, origin: 'Elkhart, IN', destination: 'Fun Town - Wharton, TX' },
      { bol: '9068', date: '2020-12-14', miles: 1222, lineHaul: 2016.30, total: 1877.47, origin: 'Elkhart, IN', destination: 'Fun Town - Wharton, TX' },
      { bol: 'FT166035', date: '2020-12-17', miles: 850, lineHaul: 1289.64, total: 1252.88, origin: 'Cleburne, TX', destination: 'Flagstaff, AZ' },
      { bol: '10766', date: '2020-12-22', miles: 782, lineHaul: 1032.24, total: 957.62, origin: 'Elkhart, IN', destination: 'American Family - Chesapeake, VA' },
    ];

    const newSettlements: ParsedSettlement[] = dynamic2020Data.map((load) => ({
      id: crypto.randomUUID(),
      company: 'Dynamic' as CompanyType,
      date: load.date,
      controlNumber: load.bol,
      terminal: 'DYN',
      origin: load.origin,
      destination: load.destination,
      miles: load.miles,
      lineHaul: load.lineHaul,
      fuelSurcharge: 0,
      tolls: 0,
      otherPay: 0,
      totalPay: load.total,
      deductions: load.lineHaul - load.total,
      checkAmount: load.total,
      selected: true
    }));

    setParsedSettlements(prev => [...prev, ...newSettlements]);
    setError(null);
  };

  // Import selected settlements with duplicate protection
  const handleImport = async () => {
    // Only import from filtered list (respects year filter)
    const selected = filteredSettlements.filter(s => s.selected);
    if (selected.length === 0) return;
    
    setIsLoading(true);
    let success = 0;
    let errors = 0;
    let skipped = 0;
    
    for (const settlement of selected) {
      // Note: We no longer skip duplicates automatically - user can force import if they selected it
      // The warning is shown in the UI, but user chose to import anyway
      
      try {
        // Build a rich description with all available info
        let description = '';
        const milesStr = settlement.miles > 0 ? ` (${settlement.miles.toLocaleString()} miles)` : '';
        const routeStr = settlement.origin && settlement.destination 
          ? `${settlement.origin} to ${settlement.destination}` 
          : settlement.destination || settlement.origin || '';
        
        if (settlement.company === 'Bennett') {
          const terminalStr = settlement.terminal ? `${settlement.terminal} Terminal` : '';
          description = `Bennett Settlement${terminalStr ? ` - ${terminalStr}` : ''}${routeStr ? ` - ${routeStr}` : ''}${milesStr}`;
        } else if (settlement.company === 'Auto Driveaway') {
          description = `Auto Driveaway Settlement - BOL ${settlement.controlNumber}${milesStr}`;
        } else if (settlement.company === 'Coldiron') {
          description = `Coldiron Settlement - Invoice ${settlement.controlNumber}${routeStr ? ` - ${routeStr}` : ''}${milesStr}`;
        } else if (settlement.company === 'Quality Driveaway') {
          description = `Quality Driveaway - Order ${settlement.controlNumber}${routeStr ? ` - ${routeStr}` : ''}${milesStr}`;
        } else {
          // Generic - try to include whatever info we have
          description = `Settlement #${settlement.controlNumber}${routeStr ? ` - ${routeStr}` : ''}${milesStr}`;
        }
        
        const filename = settlement.company === 'Bennett'
          ? `settlement-${settlement.controlNumber}-${(settlement.terminal || 'unk').toLowerCase()}.pdf`
          : `settlement-${settlement.company.toLowerCase().replace(/\s+/g, '-')}-${settlement.controlNumber}.pdf`;
        
        // FIXED: Save actual PDF file if we have it, otherwise save full extracted text
        let fileToSave: File | Blob;
        let fileType: string;
        
        if (uploadedFile && selected.length === 1) {
          // Single settlement from uploaded PDF - save the original PDF
          fileToSave = uploadedFile;
          fileType = 'application/pdf';
        } else if (uploadedFile && selected.length > 1) {
          // Multiple settlements from one PDF - save full extracted text as fallback
          // Create a rich text representation of the settlement data
          const settlementText = `${settlement.company} Settlement
Control #: ${settlement.controlNumber}
Date: ${settlement.date}
Terminal: ${settlement.terminal || 'N/A'}

Route: ${settlement.origin || 'N/A'} → ${settlement.destination || 'N/A'}
Miles: ${settlement.miles.toLocaleString()}

PAY BREAKDOWN:
Line Haul: $${(settlement.lineHaul || 0).toFixed(2)}
Fuel Surcharge: $${(settlement.fuelSurcharge || 0).toFixed(2)}
Tolls: $${(settlement.tolls || 0).toFixed(2)}
Other Pay: $${(settlement.otherPay || 0).toFixed(2)}
-------------------
TOTAL PAY: $${(settlement.totalPay || 0).toFixed(2)}
Deductions: $${(settlement.deductions || 0).toFixed(2)}
-------------------
CHECK AMOUNT: $${(settlement.checkAmount || 0).toFixed(2)}

--- Original Extracted Text ---
${pdfText}`;
          fileToSave = new Blob([settlementText], { type: 'text/plain' });
          fileType = 'text/plain';
        } else {
          // No uploaded file (pasted text) - save formatted settlement data
          const settlementText = `${settlement.company} Settlement
Control #: ${settlement.controlNumber}
Date: ${settlement.date}
Terminal: ${settlement.terminal || 'N/A'}

Route: ${settlement.origin || 'N/A'} → ${settlement.destination || 'N/A'}
Miles: ${(settlement.miles || 0).toLocaleString()}

PAY BREAKDOWN:
Line Haul: $${(settlement.lineHaul || 0).toFixed(2)}
Fuel Surcharge: $${(settlement.fuelSurcharge || 0).toFixed(2)}
Tolls: $${(settlement.tolls || 0).toFixed(2)}
Other Pay: $${(settlement.otherPay || 0).toFixed(2)}
-------------------
TOTAL PAY: $${(settlement.totalPay || 0).toFixed(2)}
Deductions: $${(settlement.deductions || 0).toFixed(2)}
-------------------
CHECK AMOUNT: $${(settlement.checkAmount || 0).toFixed(2)}

--- Original Text ---
${pdfText}`;
          fileToSave = new Blob([settlementText], { type: 'text/plain' });
          fileType = 'text/plain';
        }
        
        // Determine the date to use - override year if targetYear is set
        let dateToUse = settlement.date;
        if (targetYear !== 'auto' && settlement.date) {
          // Replace the year portion of the date (YYYY-MM-DD format)
          const dateParts = settlement.date.split('-');
          if (dateParts.length === 3) {
            dateToUse = `${targetYear}-${dateParts[1]}-${dateParts[2]}`;
          }
        }
        
        await addExpense({
          category: 'settlements',
          description,
          amount: settlement.totalPay,
          date: dateToUse,
          file: fileToSave,
          filename,
          freightBillNumber: settlement.controlNumber,
          // Trip data for Trip Calendar
          miles: settlement.miles,
          origin: settlement.origin,
          destination: settlement.destination,
          company: settlement.company,
        });
        success++;
      } catch (e) {
        console.error('Failed to import settlement:', e);
        errors++;
      }
    }
    
    setImportStatus({ success, errors, skipped });
    if (success > 0 || skipped > 0) {
      // Remove successfully imported AND skipped duplicates from the list
      const importedOrSkipped = selected.map(s => s.controlNumber);
      setParsedSettlements(prev => prev.filter(s => !importedOrSkipped.includes(s.controlNumber)));
    }
    setUploadedFile(null); // Clear the uploaded file
    setIsLoading(false);
  };

  // Year filter for multi-year documents
  const [filterYear, setFilterYear] = useState<number | 'all'>('all');
  
  // Get unique years from parsed settlements (for filtering)
  const parsedYears = [...new Set(parsedSettlements.map(s => {
    const year = s.date ? parseInt(s.date.split('-')[0]) : null;
    return year;
  }).filter(y => y !== null && y >= 2020 && y <= new Date().getFullYear()))].sort((a, b) => (b || 0) - (a || 0));
  
  // Filter settlements by selected year
  const filteredSettlements = filterYear === 'all' 
    ? parsedSettlements 
    : parsedSettlements.filter(s => s.date && s.date.startsWith(filterYear.toString()));
  
  // Count selected items (including potential duplicates that user chose to import anyway)
  const selectedCount = filteredSettlements.filter(s => s.selected).length;
  const totalAmount = filteredSettlements
    .filter(s => s.selected)
    .reduce((sum, s) => sum + s.totalPay, 0);
  const duplicateCount = filteredSettlements.filter(s => isDuplicate(s)).length;
  const selectedDuplicates = filteredSettlements.filter(s => s.selected && isDuplicate(s)).length;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">Import Settlements</h1>
        <p className="text-shuttle-sky mt-1">
          Upload settlement PDFs from Bennett, Auto Driveaway, or other carriers
        </p>
      </div>

      {/* Target Tax Year Selector */}
      <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 backdrop-blur-md rounded-2xl p-4 border border-amber-500/30">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="font-semibold text-white flex items-center gap-2">
              📅 Target Tax Year
            </h3>
            <p className="text-xs text-white/60">
              {targetYear === 'auto' 
                ? 'Year will be detected from settlement dates' 
                : `All imports will be saved to ${targetYear}`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={targetYear}
              onChange={(e) => setTargetYear(e.target.value === 'auto' ? 'auto' : parseInt(e.target.value))}
              className="px-4 py-2 bg-shuttle-dark border-2 border-amber-500/50 rounded-xl text-white font-medium focus:ring-2 focus:ring-amber-500 transition-all"
            >
              <option value="auto">🔄 Auto-detect from date</option>
              {availableYears.map(year => (
                <option key={year} value={year}>
                  📋 {year} {year < currentYear ? '(Back taxes)' : ''}
                </option>
              ))}
            </select>
          </div>
        </div>
        {targetYear !== 'auto' && targetYear < currentYear && (
          <div className="mt-3 p-2 bg-orange-500/20 rounded-lg border border-orange-500/30">
            <p className="text-sm text-orange-200">
              ⚠️ Importing to <strong>{targetYear}</strong> - make sure these settlements are from that year!
            </p>
          </div>
        )}
      </div>

      {/* Supported Companies */}
      <div className="flex flex-wrap gap-2">
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/50">
          ✓ Bennett
        </span>
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-purple-400 border border-purple-500/50">
          ✓ Auto Driveaway
        </span>
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-orange-500/20 text-orange-400 border border-orange-500/50">
          ✓ Coldiron
        </span>
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/50">
          ✓ Quality Driveaway
        </span>
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-cyan-500/20 text-cyan-400 border border-cyan-500/50">
          ✓ Dynamic
        </span>
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-amber-500/20 text-amber-400 border border-amber-500/50">
          ✓ Horizon
        </span>
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-teal-500/20 text-teal-400 border border-teal-500/50">
          ✓ Lakefront (Hotshot)
        </span>
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-500/20 text-gray-400 border border-gray-500/50">
          + Other Carriers
        </span>
      </div>

      {/* Upload Zone */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all ${
          isDragActive 
            ? 'border-shuttle-sand bg-shuttle-sand/10' 
            : 'border-white/30 hover:border-white/50 bg-white/5'
        }`}
      >
        <input {...getInputProps()} />
        <FileUp className="w-12 h-12 mx-auto mb-4 text-shuttle-sky" />
        {isDragActive ? (
          <p className="text-white text-lg">Drop the PDF here...</p>
        ) : (
          <>
            <p className="text-white text-lg mb-2">
              Drag & drop your settlement PDF here
            </p>
            <p className="text-white/60 text-sm">or click to browse</p>
          </>
        )}
      </div>

      {/* Manual Text Input */}
      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
        <h3 className="text-lg font-medium text-white mb-3 flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Or Paste Settlement Text
        </h3>
        <textarea
          value={pdfText}
          onChange={(e) => setPdfText(e.target.value)}
          placeholder="Paste settlement text from any carrier - Bennett, Auto Driveaway, or others..."
          className="w-full h-32 bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-shuttle-sand resize-none font-mono text-sm"
        />
        <div className="flex gap-3 mt-3">
          <button
            onClick={handleParseText}
            disabled={!pdfText.trim()}
            className="px-6 py-2 bg-shuttle-sky/20 text-shuttle-sky rounded-lg hover:bg-shuttle-sky/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Parse Text
          </button>
          <button
            onClick={() => { setPdfText(''); setParsedSettlements([]); setError(null); setImportStatus(null); }}
            disabled={!pdfText.trim() && parsedSettlements.length === 0}
            className="px-6 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Clear
          </button>
          <button
            onClick={handleBulkImportLakefront2020}
            className="px-6 py-2 bg-teal-500/20 text-teal-400 rounded-lg hover:bg-teal-500/30 transition-colors ml-auto"
            title="Import all 24 Lakefront 2020 loads from summary"
          >
            📦 Lakefront 2020 (24)
          </button>
          <button
            onClick={handleBulkImportDynamic2020}
            className="px-6 py-2 bg-orange-500/20 text-orange-400 rounded-lg hover:bg-orange-500/30 transition-colors"
            title="Import all 18 Dynamic 2020 trips - $25,933.87"
          >
            🚐 Dynamic 2020 (18)
          </button>
          <button
            onClick={() => setShowManualEntry(!showManualEntry)}
            className="px-6 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors"
          >
            ✏️ Manual Entry
          </button>
        </div>
      </div>

      {/* Manual Entry Form */}
      {showManualEntry && (
        <div className="bg-green-500/10 backdrop-blur-md rounded-2xl p-6 border border-green-500/30">
          <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
            ✏️ {editingSettlementId ? 'Edit Settlement' : 'Manual Settlement Entry'}
            <span className="text-sm text-white/60 font-normal ml-2">
              {editingSettlementId ? 'Modify the settlement details' : 'When PDF parsing fails'}
            </span>
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm text-white/60 mb-1">Company</label>
              <select
                value={manualEntry.company}
                onChange={(e) => setManualEntry(prev => ({ ...prev, company: e.target.value as CompanyType }))}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white"
              >
                <option value="Lakefront">Lakefront (Hotshot)</option>
                <option value="Bennett">Bennett</option>
                <option value="Auto Driveaway">Auto Driveaway</option>
                <option value="Dynamic">Dynamic</option>
                <option value="Quality Driveaway">Quality Driveaway</option>
                <option value="Coldiron">Coldiron</option>
                <option value="Horizon">Horizon</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-white/60 mb-1">Date *</label>
              <input
                type="date"
                value={manualEntry.date}
                onChange={(e) => setManualEntry(prev => ({ ...prev, date: e.target.value }))}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-white/60 mb-1">Gross Pay *</label>
              <input
                type="number"
                step="0.01"
                placeholder="2000.00"
                value={manualEntry.grossPay}
                onChange={(e) => setManualEntry(prev => ({ ...prev, grossPay: e.target.value }))}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white placeholder:text-white/30"
              />
            </div>
            <div>
              <label className="block text-sm text-white/60 mb-1">Net Pay (optional)</label>
              <input
                type="number"
                step="0.01"
                placeholder="Auto: Gross - 25% - $350"
                value={manualEntry.netPay}
                onChange={(e) => setManualEntry(prev => ({ ...prev, netPay: e.target.value }))}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white placeholder:text-white/30"
              />
            </div>
            <div>
              <label className="block text-sm text-white/60 mb-1">Load/Control #</label>
              <input
                type="text"
                placeholder="9019542"
                value={manualEntry.controlNumber}
                onChange={(e) => setManualEntry(prev => ({ ...prev, controlNumber: e.target.value }))}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white placeholder:text-white/30"
              />
            </div>
            <div>
              <label className="block text-sm text-white/60 mb-1">Miles</label>
              <input
                type="number"
                placeholder="1021"
                value={manualEntry.miles}
                onChange={(e) => setManualEntry(prev => ({ ...prev, miles: e.target.value }))}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white placeholder:text-white/30"
              />
            </div>
            <div>
              <label className="block text-sm text-white/60 mb-1">Origin</label>
              <input
                type="text"
                placeholder="Spanish Fort, AL"
                value={manualEntry.origin}
                onChange={(e) => setManualEntry(prev => ({ ...prev, origin: e.target.value }))}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white placeholder:text-white/30"
              />
            </div>
            <div>
              <label className="block text-sm text-white/60 mb-1">Destination</label>
              <input
                type="text"
                placeholder="East China, MI"
                value={manualEntry.destination}
                onChange={(e) => setManualEntry(prev => ({ ...prev, destination: e.target.value }))}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white placeholder:text-white/30"
              />
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button
              onClick={handleManualEntry}
              disabled={!manualEntry.date || !manualEntry.grossPay}
              className="px-6 py-2 bg-green-500/30 text-green-400 rounded-lg hover:bg-green-500/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {editingSettlementId ? 'Update Settlement' : 'Add Settlement'}
            </button>
            <button
              onClick={() => {
                setShowManualEntry(false);
                setEditingSettlementId(null);
                setManualEntry({
                  company: 'Lakefront',
                  date: '',
                  controlNumber: '',
                  origin: '',
                  destination: '',
                  miles: '',
                  grossPay: '',
                  netPay: ''
                });
              }}
              className="px-6 py-2 bg-white/10 text-white/60 rounded-lg hover:bg-white/20 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-shuttle-sand" />
          <span className="ml-3 text-white">Processing...</span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-red-200">{error}</p>
        </div>
      )}

      {/* Import Status */}
      {importStatus && (
        <div className={`${importStatus.success > 0 ? 'bg-green-500/20 border-green-500/50' : 'bg-yellow-500/20 border-yellow-500/50'} border rounded-xl p-4 flex items-start gap-3`}>
          <CheckCircle className={`w-5 h-5 ${importStatus.success > 0 ? 'text-green-400' : 'text-yellow-400'} flex-shrink-0 mt-0.5`} />
          <div>
            <p className={importStatus.success > 0 ? 'text-green-200' : 'text-yellow-200'}>
              {importStatus.success > 0 && `Successfully imported ${importStatus.success} settlement(s)!`}
              {importStatus.errors > 0 && ` ${importStatus.errors} failed.`}
            </p>
            {importStatus.skipped > 0 && (
              <p className="text-yellow-300 text-sm mt-1">
                ⚠️ {importStatus.skipped} duplicate(s) skipped - already in database!
              </p>
            )}
          </div>
        </div>
      )}

      {/* Parsed Settlements */}
      {parsedSettlements.length > 0 && (
        <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 overflow-hidden">
          {/* Year Filter - only show if multiple years detected */}
          {parsedYears.length > 1 && (
            <div className="p-3 bg-gradient-to-r from-blue-500/20 to-purple-500/20 border-b border-white/10">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-white/70 text-sm font-medium">📅 Filter by Year:</span>
                <button
                  onClick={() => setFilterYear('all')}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${
                    filterYear === 'all'
                      ? 'bg-white/20 text-white border border-white/40'
                      : 'bg-white/5 text-white/60 border border-white/20 hover:bg-white/10'
                  }`}
                >
                  All Years ({parsedSettlements.length})
                </button>
                {parsedYears.map(year => {
                  const yearCount = parsedSettlements.filter(s => s.date?.startsWith(year?.toString() || '')).length;
                  return (
                    <button
                      key={year}
                      onClick={() => setFilterYear(year || 'all')}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${
                        filterYear === year
                          ? 'bg-amber-500/30 text-amber-300 border border-amber-500/50'
                          : 'bg-white/5 text-white/60 border border-white/20 hover:bg-white/10'
                      }`}
                    >
                      {year} ({yearCount})
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          
          <div className="p-4 border-b border-white/10 flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="text-lg font-medium text-white">
                {filterYear === 'all' 
                  ? `Found ${parsedSettlements.length} Settlement(s)`
                  : `Showing ${filteredSettlements.length} of ${parsedSettlements.length} (${filterYear} only)`
                }
              </h3>
              {duplicateCount > 0 && (
                <p className="text-yellow-400 text-sm">
                  ⚠️ {duplicateCount} already in database
                </p>
              )}
            </div>
            <div className="flex items-center gap-4">
              <span className="text-shuttle-sky">
                {selectedCount} new • ${totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
              <button
                onClick={handleImport}
                disabled={selectedCount === 0 || isLoading}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-shuttle-sand to-amber-500 text-shuttle-dark rounded-lg font-medium hover:from-amber-400 hover:to-amber-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <Save className="w-4 h-4" />
                Import Selected
              </button>
            </div>
          </div>

          <div className="divide-y divide-white/10">
            {filteredSettlements.map((settlement) => {
              const isExisting = isDuplicate(settlement);
              return (
              <div
                key={settlement.id}
                className={`p-4 flex items-center gap-4 transition-colors ${
                  isExisting ? 'bg-yellow-500/10 opacity-60' : settlement.selected ? 'bg-white/5' : 'bg-transparent opacity-50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={settlement.selected}
                  onChange={() => toggleSelection(settlement.id)}
                  className={`w-5 h-5 rounded border-white/30 bg-white/10 text-shuttle-sand focus:ring-shuttle-sand`}
                />
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-2xl">{isExisting ? '⚠️' : '💰'}</span>
                    {isExisting && (
                      <span className="px-2 py-0.5 rounded text-xs font-medium border bg-yellow-500/20 text-yellow-400 border-yellow-500/50">
                        DUPLICATE
                      </span>
                    )}
                    <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getCompanyColor(settlement.company)}`}>
                      <Building2 className="w-3 h-3 inline mr-1" />
                      {settlement.company}
                    </span>
                    {settlement.terminal && settlement.terminal !== 'N/A' && (
                      <>
                        <span className="text-white/50">•</span>
                        <span className="font-medium text-white">
                          {settlement.terminal} Terminal
                        </span>
                      </>
                    )}
                    <span className="text-white/50">•</span>
                    <span className="text-white/70">
                      #{settlement.controlNumber}
                    </span>
                    <span className="text-white/50">•</span>
                    <span className="text-white/70">
                      {settlement.date}
                    </span>
                  </div>
                  {(settlement.origin || settlement.destination) && (
                    <p className="text-white/60 text-sm truncate">
                      {settlement.origin} → {settlement.destination} • {settlement.miles.toLocaleString()} miles
                    </p>
                  )}
                  {settlement.miles > 0 && !settlement.origin && (
                    <p className="text-white/60 text-sm">
                      {settlement.miles.toLocaleString()} miles
                    </p>
                  )}
                  <div className="flex gap-4 mt-1 text-xs text-white/50 flex-wrap">
                    {settlement.lineHaul > 0 && <span>Line Haul: ${settlement.lineHaul.toFixed(2)}</span>}
                    {settlement.fuelSurcharge > 0 && <span>Fuel: ${settlement.fuelSurcharge.toFixed(2)}</span>}
                    {settlement.tolls > 0 && <span>Tolls: ${settlement.tolls.toFixed(2)}</span>}
                    {settlement.otherPay > 0 && <span>Other: ${settlement.otherPay.toFixed(2)}</span>}
                  </div>
                </div>

                <div className="text-right">
                  <p className="text-xl font-bold text-green-400">
                    ${settlement.totalPay.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-xs text-white/50">
                    Check: ${settlement.checkAmount.toFixed(2)}
                  </p>
                </div>

                <button
                  onClick={() => handleEditSettlement(settlement)}
                  className="p-2 text-white/40 hover:text-blue-400 transition-colors"
                  title="Edit"
                >
                  <Edit2 className="w-5 h-5" />
                </button>
                <button
                  onClick={() => removeSettlement(settlement.id)}
                  className="p-2 text-white/40 hover:text-red-400 transition-colors"
                  title="Delete"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            );
            })}
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
        <h3 className="text-lg font-medium text-white mb-3">📋 Supported Settlement Formats</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-blue-500/10 rounded-lg p-4 border border-blue-500/30">
            <h4 className="font-medium text-blue-400 mb-2">🚛 Bennett</h4>
            <ul className="text-sm text-white/70 space-y-1">
              <li>• SETTLEMENT PAY format</li>
              <li>• Control # and Terminal codes</li>
              <li>• Line Haul, Fuel Surcharge, Tolls</li>
            </ul>
          </div>
          <div className="bg-purple-500/10 rounded-lg p-4 border border-purple-500/30">
            <h4 className="font-medium text-purple-400 mb-2">🚗 Auto Driveaway</h4>
            <ul className="text-sm text-white/70 space-y-1">
              <li>• BOL format (###-######-###)</li>
              <li>• Per mile breakdown</li>
              <li>• Advance and Settlement totals</li>
            </ul>
          </div>
          <div className="bg-orange-500/10 rounded-lg p-4 border border-orange-500/30">
            <h4 className="font-medium text-orange-400 mb-2">🚚 Coldiron</h4>
            <ul className="text-sm text-white/70 space-y-1">
              <li>• Remittance Advice format</li>
              <li>• Invoice # and Driver Card</li>
              <li>• Driver Pay, Fuel, Deadhead</li>
            </ul>
          </div>
          <div className="bg-green-500/10 rounded-lg p-4 border border-green-500/30">
            <h4 className="font-medium text-green-400 mb-2">🚐 Quality Driveaway</h4>
            <ul className="text-sm text-white/70 space-y-1">
              <li>• Driver # and Order # format</li>
              <li>• Mileage Pay / Flat Rate</li>
              <li>• Taxable Earnings breakdown</li>
            </ul>
          </div>
          <div className="bg-cyan-500/10 rounded-lg p-4 border border-cyan-500/30">
            <h4 className="font-medium text-cyan-400 mb-2">🏕️ Dynamic</h4>
            <ul className="text-sm text-white/70 space-y-1">
              <li>• Travel Trailer transport</li>
              <li>• BOL # with Ship From/To</li>
              <li>• Miles × Rate + Bond deductions</li>
            </ul>
          </div>
          <div className="bg-amber-500/10 rounded-lg p-4 border border-amber-500/30">
            <h4 className="font-medium text-amber-400 mb-2">🌅 Horizon</h4>
            <ul className="text-sm text-white/70 space-y-1">
              <li>• RV Transport settlements</li>
              <li>• Trip confirmations</li>
              <li>• Per mile & flat rate</li>
            </ul>
          </div>
          <div className="bg-teal-500/10 rounded-lg p-4 border border-teal-500/30">
            <h4 className="font-medium text-teal-400 mb-2">🔥 Lakefront (Hotshot)</h4>
            <ul className="text-sm text-white/70 space-y-1">
              <li>• Pay Breakdown Sheets</li>
              <li>• Rate Confirmations (A-1, Armstrong, etc.)</li>
              <li>• Gross - 25% cut - insurance = net</li>
            </ul>
          </div>
        </div>
        <p className="mt-4 text-white/50 text-xs">
          💡 Just paste or upload any settlement - the parser auto-detects the format!
        </p>
      </div>
    </div>
  );
}
